import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Flex, Text, IconButton, HStack, VStack, Badge, Menu, MenuButton, MenuList, MenuItem, MenuDivider,
  Button, useColorMode, useColorModeValue, Avatar, Drawer, DrawerOverlay, DrawerContent, DrawerHeader,
  DrawerBody, DrawerCloseButton, useDisclosure, useToast, keyframes, Collapse, Tooltip, InputGroup,
  InputLeftElement, Input, Tabs, TabList, TabPanels, Tab, TabPanel, Divider, Tag, TagLeftIcon,
  useBreakpointValue, useToken,
} from '@chakra-ui/react';
import { Global } from '@emotion/react';
import {
  FaMoon, FaSun, FaBell, FaUserCircle, FaSignOutAlt, FaCloudSun, FaClock, FaBars,
  FaCheckCircle, FaTimesCircle, FaClipboardList, FaChevronRight,
  FaChevronDown, FaCalendarCheck, FaExclamationTriangle, FaSearch, FaExternalLinkAlt, FaTrash, FaCheck,
  FaMoneyBillWave, FaChartPie, FaCog, FaChartBar, FaHeadset, FaFileInvoiceDollar, FaTable,
  FaWallet, FaEnvelope, FaUserShield, FaLock, FaFolderOpen,
  FaExchangeAlt, FaTachometerAlt, FaShoppingCart, FaCreditCard, FaUserTie, FaBalanceScale, FaDoorOpen, FaUniversity,
  FaCalendarAlt, FaComments, FaListUl, FaReceipt, FaCoins, FaLayerGroup, FaRobot, FaPercentage, FaClipboardCheck,
  FaBullhorn,
} from 'react-icons/fa';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import WeatherComponent from './WeatherComponent';
import { UseDataUserContext } from '../../_Hooks/_Context/useDataUserAuthenticated';
import { UiSectionContext, UI_CONTEXTS } from '../../_Hooks/_Context/useUiSectionContext';
import { UiPreferencesProvider } from '../../_Hooks/_Context/useUiPreferences';
import HeaderV2 from './HeaderV2';
import { ApiConsume } from '../../_API/ApiConsume';
import { CONTENT_MAX_W, SIDEBAR_COL_W, SIDEBAR_EXP_W, HEADER_HEIGHT } from '../../_UI/layoutConstants';
import { openUrl } from '../../_Utils/mobileBridge';
import { traceWamNav } from '../../_Utils/wamTraceNav';
import { clearAuthStorage } from '../../Helpers/authStorage';
import useAdminNotifyStream from '../../_Hooks/useAdminNotifyStream';
import { canAccessRoutePath, canAccessSidebarItem, isCapabilitiesStrictDefaultDeny } from '../../_Config/capabilityMap';
import { NotFoundPage } from './NotFoundPage';
import { AsistenteProvider } from './Asistente/AsistenteContext';
import AsistentePanel from './Asistente/AsistentePanel';
import AsistenteMain from './Asistente/AsistenteMain';

/* ================= Constantes de layout y rutas ================= */
// Rutas solicitadas
const ROUTES = {
  COBRANZAS: '/panel/movimientos/listado/cobranzas',
  RECLAMOS : '/panel/reclamos',
  RESERVAS : '/panel/servicios/reservas/CalendarSimple',
};

const NAV_CONTEXT_META = [
  { key: UI_CONTEXTS.CONSORCIOS, label: 'Consorcios' },
  { key: UI_CONTEXTS.MI_ADMIN, label: 'Mi Administración' },
];

const SIDEBAR_GROUP_META = [
  { key: 'principal', label: 'Principal', section: UI_CONTEXTS.CONSORCIOS },
  { key: 'operacion', label: 'Operación', section: UI_CONTEXTS.CONSORCIOS },
  { key: 'expenses_providers', label: 'Gastos y proveedores', section: UI_CONTEXTS.CONSORCIOS },
  { key: 'collections_income', label: 'Cobranzas e ingresos', section: UI_CONTEXTS.CONSORCIOS },
  { key: 'payments_expenses', label: 'Pagos y egresos', section: UI_CONTEXTS.CONSORCIOS },
  { key: 'finance', label: 'Finanzas', section: UI_CONTEXTS.CONSORCIOS },
  { key: 'liquidations', label: 'Liquidaciones', section: UI_CONTEXTS.CONSORCIOS },
  { key: 'salary', label: 'Sueldos', section: UI_CONTEXTS.CONSORCIOS },
  { key: 'communication', label: 'Comunicación', section: UI_CONTEXTS.CONSORCIOS },
  { key: 'reports', label: 'Reportes', section: UI_CONTEXTS.CONSORCIOS },
  { key: 'administration', label: 'Administración', section: UI_CONTEXTS.CONSORCIOS },
  { key: 'audit', label: 'Auditoría y cumplimiento', section: UI_CONTEXTS.CONSORCIOS },
  { key: 'backoffice', label: 'Backoffice', section: UI_CONTEXTS.CONSORCIOS },
  { key: 'mi_admin', label: 'Mi Administración', section: UI_CONTEXTS.MI_ADMIN },
];

const CONSORCIOS_PRIMARY_NAV_KEYS = [
  'nav_dashboard',
  'nav_asistente',
  'nav_consorcio_summary',
  'nav_expenses_area',
  'nav_payments',
  'nav_collections_area',
  'nav_bank_reconciliation',
  'nav_liquidations',
  'nav_reports',
  'nav_current_accounts',
  'nav_salary',
  'nav_finance',
  'nav_services',
  'nav_claims',
  'nav_mail',
  'nav_communication',
  'nav_configuration',
  'nav_documents',
  'nav_audit',
  'nav_manual',
  'nav_novedades_wam',
  'nav_backoffice',
];

const MI_ADMIN_PRIMARY_NAV_KEYS = ['contab_admin'];

const normalizeContabTab = (slug) => {
  if (!slug) return 'dashboard';
  const map = {
    dashboard: 'dashboard',
    home: 'dashboard',
    config: 'config',
    configuracion: 'config',
    apertura: 'apertura',
    periodos: 'periodos',
    plan: 'plan',
    cuentas: 'plan',
    ventas: 'ventas',
    cobranzas: 'cobranzas',
    compras: 'compras',
    pagos: 'pagos',
    sueldos: 'sueldos',
    conciliacion: 'conciliacion',
    'ctas-ctes': 'ctasctes',
    ctasctes: 'ctasctes',
    ctacte: 'ctasctes',
    impuestos: 'impuestos',
    iva: 'iva',
    iibb: 'iibb',
    ganancias: 'ganancias_pf',
    ganancias_pf: 'ganancias_pf',
    auditoria: 'auditoria',
    mayor: 'mayor',
    mayores: 'mayor',
  };
  return map[slug] || slug;
};

const isConsorciosRoutePath = (pathname) => {
  if (!pathname) return false;
  return pathname === '/' || pathname === '/dashboard' || pathname.startsWith('/panel/');
};

const extractTabFromLink = (href) => {
  if (!href || typeof href !== 'string') return null;
  const parts = href.split('?');
  if (parts.length < 2) return null;
  try {
    const params = new URLSearchParams(parts[1]);
    return (params.get('tab') || '').toLowerCase() || null;
  } catch {
    return null;
  }
};

const normalizeNavLink = (link) => (typeof link === 'string' ? link.split('?')[0] : '');

const doesPathMatchNavPath = (pathname, link, activePrefix, matchSubroutes = true) => {
  const normalizedLink = normalizeNavLink(link);
  if (!pathname || !normalizedLink) return false;
  if (normalizedLink === '/') {
    return pathname === '/' || pathname === '/dashboard';
  }
  if (pathname === normalizedLink) return true;
  const prefixes = Array.isArray(activePrefix)
    ? activePrefix
    : activePrefix
      ? [activePrefix]
      : [];
  if (prefixes.some((prefix) => prefix && pathname.startsWith(prefix))) {
    return true;
  }
  if (!matchSubroutes) {
    return false;
  }
  return pathname.startsWith(`${normalizedLink}/`);
};

const isNavItemActive = ({ item, pathname, isContabRoute, contabActiveTab }) => {
  const normalizedLink = normalizeNavLink(item?.link);
  if (!normalizedLink) return false;
  if (normalizedLink === '/contabilidad-admin') {
    if (!isContabRoute) return false;
    const itemTab = normalizeContabTab(extractTabFromLink(item.link) || 'dashboard');
    if ((item.link || '').includes('?tab=')) {
      return itemTab === (contabActiveTab || 'dashboard');
    }
    return true;
  }
  return doesPathMatchNavPath(pathname, item.link, item.activePrefix, item.matchSubroutes);
};

export const NAV_ITEMS = [
  {
    key: 'nav_dashboard',
    title: 'Inicio',
    navTitle: 'Dashboard',
    link: '/',
    icon: <FaChartPie />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'principal',
  },
  {
    key: 'nav_consorcio_summary',
    title: 'Resumen de Consorcio',
    link: '/panel/resumen-consorcio',
    icon: <FaTable />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'principal',
    activePrefix: ['/panel/resumen-consorcio', '/panel/propietarios/hub', '/panel/propietario/hub'],
  },
  {
    key: 'nav_claims',
    title: 'Reclamos',
    link: '/panel/reclamos',
    icon: <FaHeadset />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'operacion',
    capabilityKey: 'nav_claims_area',
    activePrefix: [
      '/panel/reclamos',
      '/panel/servicios/reclamos',
      '/panel/servicios/listado/proveedores-asignados',
      '/panel/servicios/proveedores-asignados/seguimiento',
      '/panel/servicios/reclamos-archivados',
    ],
  },
  {
    key: 'nav_services',
    title: 'Servicios',
    link: '/panel/servicios',
    icon: <FaCalendarCheck />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'operacion',
    activePrefix: [
      '/panel/tareas',
      '/panel/servicios/reservas',
      '/panel/servicios/listado/amenitis',
      '/panel/servicios/amenitis',
      '/panel/servicios/asambleas',
      '/panel/servicios/listado/asambleas',
      '/panel/servicios/encuestas',
      '/panel/servicios/listado/encuestas',
      '/panel/servicios/mudanzas',
      '/panel/servicios/referidos',
      '/panel/referidos',
      '/panel/servicios/solicitudes',
      '/panel/servicios/pedidos',
      '/panel/solicitudes',
      '/panel/requerimientos',
      '/panel/servicios/presupuestos',
      '/panel/servicios/gestion-alquileres',
    ],
    matchSubroutes: false,
  },
  {
    key: 'nav_assigned_providers',
    title: 'Proveedores asignados',
    link: '/panel/servicios/listado/proveedores-asignados',
    icon: <FaUserTie />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'operacion',
    activePrefix: ['/panel/servicios/listado/proveedores-asignados', '/panel/servicios/proveedores-asignados/seguimiento'],
    collapsedHidden: true,
  },
  {
    key: 'nav_tasks',
    title: 'Tareas',
    link: '/panel/tareas',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'operacion',
    activePrefix: '/panel/tareas',
  },
  {
    key: 'nav_requests',
    title: 'Solicitudes',
    link: '/panel/servicios/solicitudes',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'operacion',
    activePrefix: ['/panel/servicios/solicitudes', '/panel/solicitudes', '/panel/servicios/pedidos'],
  },
  {
    key: 'nav_reservas',
    title: 'Reservas',
    link: '/panel/servicios/reservas',
    icon: <FaCalendarCheck />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'operacion',
    activePrefix: ['/panel/servicios/reservas', '/panel/servicios/reservas/CalendarSimple'],
    collapsedHidden: true,
  },
  {
    key: 'nav_mudanzas',
    title: 'Mudanzas',
    link: '/panel/servicios/mudanzas',
    icon: <FaCalendarAlt />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'operacion',
    activePrefix: ['/panel/servicios/mudanzas'],
    collapsedHidden: true,
  },
  {
    key: 'nav_amenities',
    title: 'Amenities',
    link: '/panel/servicios/listado/amenitis',
    icon: <FaDoorOpen />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'operacion',
    activePrefix: ['/panel/servicios/listado/amenitis', '/panel/servicios/amenitis'],
    collapsedHidden: true,
  },
  {
    key: 'nav_assemblies',
    title: 'Asambleas',
    link: '/panel/servicios/listado/asambleas',
    icon: <FaCalendarAlt />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'operacion',
    activePrefix: ['/panel/servicios/listado/asambleas', '/panel/servicios/asambleas'],
    collapsedHidden: true,
  },
  {
    key: 'nav_surveys',
    title: 'Encuestas',
    link: '/panel/servicios/encuestas',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'operacion',
    activePrefix: ['/panel/servicios/encuestas', '/panel/servicios/listado/encuestas'],
    collapsedHidden: true,
  },
  {
    key: 'nav_referidos',
    title: 'Referidos',
    link: '/panel/servicios/referidos',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'operacion',
    activePrefix: '/panel/servicios/referidos',
    collapsedHidden: true,
  },
  {
    key: 'nav_expenses_area',
    title: 'Gastos',
    link: '/panel/movimientos/gastos',
    icon: <FaFileInvoiceDollar />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'expenses_providers',
    capabilityKey: 'nav_expenses_area',
    activePrefix: ['/panel/movimientos/gastos', '/panel/seguros', '/panel/planes-pago', '/panel/planes_pago'],
  },
  {
    key: 'nav_expense_drafts',
    title: 'Gastos en borrador',
    link: '/panel/movimientos/gastos/borradores',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'expenses_providers',
    activePrefix: '/panel/movimientos/gastos/borradores',
    collapsedHidden: true,
  },
  {
    key: 'nav_expenses_cuotas',
    title: 'Gastos en cuotas',
    link: '/panel/movimientos/gastos/cuotas',
    icon: <FaFileInvoiceDollar />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'expenses_providers',
    activePrefix: '/panel/movimientos/gastos/cuotas',
    collapsedHidden: true,
  },
  {
    key: 'nav_providers',
    title: 'Proveedores',
    link: '/panel/movimientos/gastos/listado/proveedores',
    icon: <FaUserTie />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'expenses_providers',
    activePrefix: ['/panel/movimientos/gastos/listado/proveedores', '/panel/movimientos/gastos/agregar/proveedor', '/panel/movimientos/gastos/editar/proveedor'],
    collapsedHidden: true,
  },
  {
    key: 'nav_provider_current_accounts',
    title: 'Cta. cte. proveedores',
    link: '/panel/movimientos/gastos/listado/cuentas-corrientes-proveedores',
    icon: <FaLayerGroup />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'expenses_providers',
    activePrefix: '/panel/movimientos/gastos/listado/cuentas-corrientes-proveedores',
    collapsedHidden: true,
  },
  {
    key: 'nav_insurance',
    title: 'Seguros',
    link: '/panel/seguros',
    icon: <FaUserShield />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'expenses_providers',
    activePrefix: '/panel/seguros',
    collapsedHidden: true,
  },
  {
    key: 'nav_payment_plans',
    title: 'Planes de pago',
    link: '/panel/planes-pago',
    icon: <FaCoins />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'expenses_providers',
    activePrefix: ['/panel/planes-pago', '/panel/planes_pago'],
    collapsedHidden: true,
  },
  {
    key: 'nav_collections_area',
    title: 'Cobranzas',
    link: '/panel/movimientos/cobranzas',
    icon: <FaMoneyBillWave />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'collections_income',
    capabilityKey: 'nav_collections_area',
    activePrefix: [
      '/panel/movimientos/cobranzas',
      '/panel/movimientos/listado/cobranzas',
      '/panel/movimientos/cobranzas/borradores',
      '/panel/movimientos/cobranzas/siro/rendiciones',
      '/panel/movimientos/otros-ingresos',
    ],
  },
  {
    key: 'nav_collections_query',
    title: 'Consulta de cobranzas',
    link: '/panel/movimientos/listado/cobranzas',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'collections_income',
    activePrefix: '/panel/movimientos/listado/cobranzas',
    collapsedHidden: true,
  },
  {
    key: 'nav_collection_drafts',
    title: 'Borradores de cobranzas',
    link: '/panel/movimientos/cobranzas/borradores',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'collections_income',
    activePrefix: '/panel/movimientos/cobranzas/borradores',
    collapsedHidden: true,
  },
  {
    key: 'nav_other_income',
    title: 'Otros ingresos',
    link: '/panel/movimientos/otros-ingresos',
    icon: <FaCoins />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'collections_income',
    activePrefix: '/panel/movimientos/otros-ingresos',
    collapsedHidden: true,
  },
  {
    key: 'nav_payments',
    title: 'Pagos a proveedores',
    link: '/panel/movimientos/pagos',
    icon: <FaCreditCard />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'payments_expenses',
    activePrefix: ['/panel/movimientos/pagos', '/panel/movimientos/listado/pagos', '/panel/finanzas/retenciones'],
  },
  {
    key: 'nav_checks',
    title: 'Cheques',
    link: '/panel/movimientos/pagos/listado/cheques',
    icon: <FaReceipt />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'payments_expenses',
    activePrefix: '/panel/movimientos/pagos/listado/cheques',
    collapsedHidden: true,
  },
  {
    key: 'nav_solpay_alta',
    title: 'Alta en SolPay',
    link: '/panel/movimientos/pagos/alta-solpay',
    icon: <FaUniversity />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'payments_expenses',
    activePrefix: '/panel/movimientos/pagos/alta-solpay',
    collapsedHidden: true,
  },
  {
    key: 'nav_withholdings',
    title: 'Retenciones',
    link: '/panel/finanzas/retenciones',
    icon: <FaBalanceScale />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'payments_expenses',
    activePrefix: '/panel/finanzas/retenciones',
    collapsedHidden: true,
  },
  {
    key: 'nav_unpaid_invoices',
    title: 'Facturas no pagadas',
    link: '/panel/reportes/facturas_no_pagadas',
    icon: <FaFileInvoiceDollar />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'payments_expenses',
    activePrefix: '/panel/reportes/facturas_no_pagadas',
    collapsedHidden: true,
  },
  {
    key: 'nav_finance',
    title: 'Finanzas',
    link: '/panel/finanzas',
    icon: <FaWallet />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'finance',
    capabilityKey: 'nav_finance',
    activePrefix: [
      '/panel/finanzas/listado/bancos',
      '/panel/finanzas/listado/cajas',
      '/panel/finanzas/listado/movimientos',
      '/panel/finanzas/no-identificados',
      '/panel/finanzas/listado/plazos-fijos',
      '/panel/finanzas/listado/monedas-extranjeras',
      '/panel/finanzas/listado/cuentas-inversion',
      '/panel/finanzas/listado/cta-corriente-banco',
      '/panel/finanzas/listado/cta-corriente-caja',
    ],
    matchSubroutes: false,
  },
  {
    key: 'nav_banks',
    title: 'Bancos',
    link: '/panel/finanzas/listado/bancos',
    icon: <FaMoneyBillWave />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'finance',
    activePrefix: '/panel/finanzas/listado/bancos',
    collapsedHidden: true,
  },
  {
    key: 'nav_cash',
    title: 'Cajas',
    link: '/panel/finanzas/listado/cajas',
    icon: <FaWallet />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'finance',
    activePrefix: '/panel/finanzas/listado/cajas',
    collapsedHidden: true,
  },
  {
    key: 'nav_financial_movements',
    title: 'Movimientos financieros',
    link: '/panel/finanzas/listado/movimientos',
    icon: <FaExchangeAlt />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'finance',
    activePrefix: '/panel/finanzas/listado/movimientos',
    collapsedHidden: true,
  },
  {
    key: 'nav_bank_reconciliation',
    title: 'Conciliación bancaria',
    link: '/panel/finanzas/listado/conc-bancaria',
    icon: <FaBalanceScale />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'finance',
    activePrefix: [
      '/panel/finanzas/listado/conc-bancaria',
      '/panel/finanzas/agregar/conc-bancaria',
      '/panel/finanzas/editar/conc-bancaria',
      '/panel/finanzas/comparacion',
      '/conciliaciones',
    ],
    collapsedHidden: true,
  },
  {
    key: 'nav_bank_reconciliation_process',
    title: 'Proceso de conciliación',
    link: '/panel/finanzas/conciliacion/proceso',
    icon: <FaExchangeAlt />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'finance',
    activePrefix: '/panel/finanzas/conciliacion/proceso',
    collapsedHidden: true,
  },
  {
    key: 'nav_unidentified_movements',
    title: 'Movimientos no identificados',
    link: '/panel/finanzas/no-identificados',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'finance',
    activePrefix: '/panel/finanzas/no-identificados',
    collapsedHidden: true,
  },
  {
    key: 'nav_fixed_terms',
    title: 'Plazos fijos',
    link: '/panel/finanzas/listado/plazos-fijos',
    icon: <FaCoins />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'finance',
    activePrefix: '/panel/finanzas/listado/plazos-fijos',
    collapsedHidden: true,
  },
  {
    key: 'nav_foreign_currency',
    title: 'Moneda extranjera',
    link: '/panel/finanzas/listado/monedas-extranjeras',
    icon: <FaCoins />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'finance',
    activePrefix: '/panel/finanzas/listado/monedas-extranjeras',
    collapsedHidden: true,
  },
  {
    key: 'nav_investment_accounts',
    title: 'Cuentas de inversión',
    link: '/panel/finanzas/listado/cuentas-inversion',
    icon: <FaLayerGroup />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'finance',
    activePrefix: '/panel/finanzas/listado/cuentas-inversion',
    collapsedHidden: true,
  },
  {
    key: 'nav_bank_current_accounts',
    title: 'Ctas. ctes. bancos',
    link: '/panel/finanzas/listado/cta-corriente-banco',
    icon: <FaLayerGroup />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'finance',
    activePrefix: '/panel/finanzas/listado/cta-corriente-banco',
    collapsedHidden: true,
  },
  {
    key: 'nav_cash_current_accounts',
    title: 'Ctas. ctes. cajas',
    link: '/panel/finanzas/listado/cta-corriente-caja',
    icon: <FaLayerGroup />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'finance',
    activePrefix: '/panel/finanzas/listado/cta-corriente-caja',
    collapsedHidden: true,
  },
  {
    key: 'nav_liquidations',
    title: 'Liquidación',
    navTitle: 'Liquidación',
    link: '/panel/liquidacion',
    icon: <FaTable />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'liquidations',
    capabilityKey: 'nav_liquidations_area',
    activePrefix: ['/panel/liquidacion', '/panel/liquidación'],
  },
  {
    key: 'nav_generate_liquidation',
    title: 'Generar liquidación',
    link: '/panel/liquidacion/agregar/liquidacion-expensa',
    icon: <FaTable />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'liquidations',
    activePrefix: ['/panel/liquidacion/agregar/liquidacion-expensa', '/panel/liquidacion/agregar/liquidación-expensa', '/panel/liquidación/agregar/liquidación-expensa'],
    collapsedHidden: true,
  },
  {
    key: 'nav_liquidation_reports',
    title: 'Informes generales',
    link: '/panel/liquidacion/informes-generales',
    icon: <FaChartBar />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'liquidations',
    activePrefix: '/panel/liquidacion/informes-generales',
    collapsedHidden: true,
  },
  {
    key: 'nav_liquidation_owner_accounts',
    title: 'Ctas. ctes. propietarios',
    link: '/panel/liquidacion/cuentacorrientes_propietarios',
    icon: <FaLayerGroup />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'liquidations',
    activePrefix: '/panel/liquidacion/cuentacorrientes_propietarios',
    collapsedHidden: true,
  },
  {
    key: 'nav_liquidation_expense_concepts',
    title: 'Conceptos de expensa',
    link: '/panel/liquidacion/conceptos-expensa',
    icon: <FaReceipt />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'liquidations',
    activePrefix: '/panel/liquidacion/conceptos-expensa',
    collapsedHidden: true,
  },
  {
    key: 'nav_liquidation_groups',
    title: 'Grupos',
    link: '/panel/liquidacion/grupos',
    icon: <FaLayerGroup />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'liquidations',
    activePrefix: '/panel/liquidacion/grupos',
    collapsedHidden: true,
  },
  {
    key: 'nav_liquidation_percentage_groups',
    title: 'Grupos porcentuales',
    link: '/panel/liquidacion/grupos-porcentuales',
    icon: <FaLayerGroup />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'liquidations',
    activePrefix: '/panel/liquidacion/grupos-porcentuales',
    collapsedHidden: true,
  },
  {
    key: 'nav_liquidation_unit_percentages',
    title: 'Porcentajes por unidad',
    link: '/panel/liquidacion/porcentajes-unidad',
    icon: <FaBalanceScale />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'liquidations',
    activePrefix: '/panel/liquidacion/porcentajes-unidad',
    collapsedHidden: true,
  },
  {
    key: 'nav_liquidation_siro',
    title: 'Configuración SIRO',
    link: '/panel/liquidacion/configuracion-siro',
    icon: <FaCog />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'liquidations',
    activePrefix: ['/panel/liquidacion/configuracion-siro', '/panel/liquidación/configuración-siro'],
    collapsedHidden: true,
  },
  {
    key: 'nav_liquidation_pme',
    title: 'Configuración PME',
    link: '/panel/liquidacion/configuracion-pme',
    icon: <FaCog />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'liquidations',
    activePrefix: ['/panel/liquidacion/configuracion-pme', '/panel/liquidación/configuración-pme', '/panel/liquidacion/pme/configuracion'],
    collapsedHidden: true,
  },
  {
    key: 'nav_recaudaciones_extraordinarias',
    title: 'Recaudaciones extraordinarias',
    link: '/panel/liquidacion/recaudaciones-extraordinarias',
    icon: <FaCoins />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'liquidations',
    activePrefix: '/panel/liquidacion/recaudaciones-extraordinarias',
    collapsedHidden: true,
  },
  {
    key: 'nav_verificacion_reporte_financiero',
    title: 'Verificación reporte financiero',
    link: '/panel/liquidacion/verificacion-reporte-financiero',
    icon: <FaClipboardCheck />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'liquidations',
    activePrefix: '/panel/liquidacion/verificacion-reporte-financiero',
    collapsedHidden: true,
  },
  {
    key: 'nav_liquidation_summary_adjustments',
    title: 'Ajustes resumen',
    link: '/panel/liquidacion/ajustes-resumen',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'liquidations',
    activePrefix: '/panel/liquidacion/ajustes-resumen',
    collapsedHidden: true,
  },
  {
    key: 'nav_salary',
    title: 'Sueldos',
    link: '/panel/sueldos',
    icon: <FaUserTie />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'salary',
    capabilityKey: 'nav_salary',
    activePrefix: '/panel/sueldos',
  },
  {
    key: 'nav_salary_employees',
    title: 'Encargados',
    link: '/panel/sueldos/listado/alta-empleados',
    icon: <FaUserTie />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'salary',
    activePrefix: '/panel/sueldos/listado/alta-empleados',
    collapsedHidden: true,
  },
  {
    key: 'nav_salary_agreements',
    title: 'Convenios',
    link: '/panel/sueldos/listado/convenios',
    icon: <FaListUl />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'salary',
    activePrefix: '/panel/sueldos/listado/convenios',
    collapsedHidden: true,
  },
  {
    key: 'nav_salary_categories',
    title: 'Categorías CCT',
    link: '/panel/sueldos/listado/categorias-cct',
    icon: <FaListUl />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'salary',
    activePrefix: ['/panel/sueldos/listado/categorias-cct', '/panel/sueldos/categorias-cct'],
    collapsedHidden: true,
  },
  {
    key: 'nav_salary_scales',
    title: 'Escalas salariales',
    link: '/panel/sueldos/listado/escalas-salariales',
    icon: <FaChartBar />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'salary',
    activePrefix: ['/panel/sueldos/listado/escalas-salariales', '/panel/sueldos/escalas-salariales'],
    collapsedHidden: true,
  },
  {
    key: 'nav_salary_alicuotas_patronales',
    title: 'Alícuotas Patronales',
    link: '/panel/sueldos/listado/alicuotas-patronales',
    icon: <FaPercentage />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'salary',
    activePrefix: ['/panel/sueldos/listado/alicuotas-patronales'],
    collapsedHidden: true,
  },
  {
    key: 'nav_salary_remunerative_concepts',
    title: 'Conceptos remunerativos',
    link: '/panel/sueldos/listado/conceptos-remunerativos',
    icon: <FaReceipt />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'salary',
    activePrefix: '/panel/sueldos/listado/conceptos-remunerativos',
    collapsedHidden: true,
  },
  {
    key: 'nav_salary_non_remunerative_concepts',
    title: 'Conceptos no remunerativos',
    link: '/panel/sueldos/listado/conceptos-no-remunerativos',
    icon: <FaReceipt />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'salary',
    activePrefix: '/panel/sueldos/listado/conceptos-no-remunerativos',
    collapsedHidden: true,
  },
  {
    key: 'nav_salary_agreement_discounts',
    title: 'Descuentos convenio',
    link: '/panel/sueldos/listado/descuento-convenio',
    icon: <FaBalanceScale />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'salary',
    activePrefix: '/panel/sueldos/listado/descuento-convenio',
    collapsedHidden: true,
  },
  {
    key: 'nav_salary_updates',
    title: 'Novedades',
    link: '/panel/sueldos/novedades',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'salary',
    activePrefix: '/panel/sueldos/novedades',
    collapsedHidden: true,
  },
  {
    // Vive en el grupo 'salary' aunque su ruta siga bajo /panel/servicios/:
    // la pantalla se gatea con el addon de cumplimiento laboral, no con las
    // capacidades de sueldos. Mover la URL daría a entender lo contrario.
    key: 'nav_labor_obligations',
    title: 'Obligaciones del personal',
    link: '/panel/servicios/obligaciones-personal',
    icon: <FaUserShield />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'salary',
    activePrefix: '/panel/servicios/obligaciones-personal',
    collapsedHidden: true,
  },
  {
    key: 'nav_salary_liquidations',
    title: 'Liquidaciones de sueldo',
    link: '/panel/sueldos/listado/liquidacion-sueldos',
    icon: <FaTable />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'salary',
    activePrefix: ['/panel/sueldos/listado/liquidacion-sueldos', '/panel/sueldos/listado/liquidación-sueldos'],
    collapsedHidden: true,
  },
  {
    key: 'nav_salary_generate',
    title: 'Generar liquidación de sueldo',
    link: '/panel/sueldos/generar-liquidacion',
    icon: <FaTable />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'salary',
    activePrefix: '/panel/sueldos/generar-liquidacion',
    collapsedHidden: true,
  },
  {
    key: 'nav_mail',
    title: 'Correo',
    link: '/panel/correo/inbox',
    icon: <FaEnvelope />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'communication',
    activePrefix: ['/panel/correo'],
  },
  {
    key: 'nav_communication',
    title: 'Comunicación',
    link: '/panel/comunicacion',
    icon: <FaComments />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'communication',
    capabilityKey: 'nav_communication_area',
    activePrefix: ['/panel/comunicacion', '/panel/chat'],
  },
  {
    key: 'nav_mail_compose',
    title: 'Redactar correo',
    link: '/panel/correo/compose',
    icon: <FaEnvelope />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'communication',
    activePrefix: '/panel/correo/compose',
    collapsedHidden: true,
  },
  {
    key: 'nav_mail_configs',
    title: 'Configuración correo',
    link: '/panel/correo/mail-configs',
    icon: <FaCog />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'communication',
    activePrefix: ['/panel/correo/mail-configs', '/panel/correo/mail-config', '/panel/correo/sendgrid-config', '/panel/correo/configuración'],
    collapsedHidden: true,
  },
  {
    key: 'nav_whatsapp',
    title: 'WhatsApp',
    link: '/panel/chat/whatsapp',
    icon: <FaRobot />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'communication',
    capabilityKey: 'nav_communication_area',
    activePrefix: '/panel/chat',
  },
  {
    key: 'nav_whatsapp_new_template',
    title: 'Nuevo template',
    link: '/panel/chat/nuevo-template',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'communication',
    activePrefix: '/panel/chat/nuevo-template',
    collapsedHidden: true,
  },
  {
    key: 'nav_whatsapp_templates',
    title: 'Plantillas WhatsApp',
    link: '/panel/chat/plantillas-whatsapp',
    icon: <FaListUl />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'communication',
    activePrefix: '/panel/chat/plantillas-whatsapp',
    collapsedHidden: true,
  },
  {
    key: 'nav_whatsapp_templates_approved',
    title: 'Plantillas aprobadas',
    link: '/panel/chat/lista-plantillas-aprobadas',
    icon: <FaCheckCircle />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'communication',
    activePrefix: '/panel/chat/lista-plantillas-aprobadas',
    collapsedHidden: true,
  },
  {
    key: 'nav_whatsapp_templates_pending',
    title: 'Plantillas pendientes',
    link: '/panel/chat/admin-plantillas-pendientes',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'communication',
    activePrefix: '/panel/chat/admin-plantillas-pendientes',
    collapsedHidden: true,
  },
  {
    key: 'nav_sales_inbox',
    title: 'Ventas Inbox',
    link: '/panel/ventas/inbox',
    icon: <FaShoppingCart />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'communication',
    onlySuperUser: true,
    activePrefix: '/panel/ventas/inbox',
    collapsedHidden: true,
  },
  {
    key: 'nav_reports',
    title: 'Reportes',
    link: '/panel/reportes',
    icon: <FaChartBar />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'reports',
    capabilityKey: 'nav_reports',
    activePrefix: '/panel/reportes',
  },
  {
    key: 'nav_documents',
    title: 'Carpetas locales',
    link: '/panel/folders/local',
    icon: <FaFolderOpen />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    capabilityKey: 'nav_documents',
    activePrefix: '/panel/folders/local',
  },
  {
    key: 'nav_report_expenses',
    title: 'Reporte de gastos',
    link: '/panel/reportes/gastos',
    icon: <FaFileInvoiceDollar />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'reports',
    activePrefix: '/panel/reportes/gastos',
    collapsedHidden: true,
  },
  {
    key: 'nav_report_payments',
    title: 'Reporte de pagos',
    link: '/panel/reportes/pagos',
    icon: <FaCreditCard />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'reports',
    activePrefix: '/panel/reportes/pagos',
    collapsedHidden: true,
  },
  {
    key: 'nav_report_other_income',
    title: 'Otros ingresos',
    link: '/panel/reportes/otros-ingresos',
    icon: <FaCoins />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'reports',
    activePrefix: '/panel/reportes/otros-ingresos',
    collapsedHidden: true,
  },
  {
    key: 'nav_report_unpaid',
    title: 'Facturas no pagadas',
    link: '/panel/reportes/facturas_no_pagadas',
    icon: <FaFileInvoiceDollar />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'reports',
    activePrefix: '/panel/reportes/facturas_no_pagadas',
    collapsedHidden: true,
  },
  {
    key: 'nav_report_financial',
    title: 'Reporte financiero',
    link: '/panel/reportes/financiero',
    icon: <FaWallet />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'reports',
    activePrefix: '/panel/reportes/financiero',
    collapsedHidden: true,
  },
  {
    key: 'nav_report_financial_global',
    title: 'Reporte financiero global',
    link: '/panel/reportes/financiero/global',
    icon: <FaChartBar />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'reports',
    activePrefix: '/panel/reportes/financiero/global',
    collapsedHidden: true,
  },
  {
    key: 'nav_report_annual',
    title: 'Informe anual',
    link: '/panel/reportes/informe-anual',
    icon: <FaCalendarAlt />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'reports',
    activePrefix: '/panel/reportes/informe-anual',
    collapsedHidden: true,
  },
  {
    key: 'nav_report_providers',
    title: 'Reporte proveedores',
    link: '/panel/reportes/proveedores',
    icon: <FaUserTie />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'reports',
    activePrefix: '/panel/reportes/proveedores',
    collapsedHidden: true,
  },
  {
    key: 'nav_report_expense_concept',
    title: 'Reporte concepto expensa',
    link: '/panel/reportes/concepto_expensa',
    icon: <FaReceipt />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'reports',
    activePrefix: '/panel/reportes/concepto_expensa',
    collapsedHidden: true,
  },
  {
    key: 'nav_report_payment_plans',
    title: 'Planes de pago',
    link: '/panel/reportes/planes-pago',
    icon: <FaCoins />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'reports',
    activePrefix: '/panel/reportes/planes-pago',
    collapsedHidden: true,
  },
  {
    key: 'nav_report_flujo_fondos',
    title: 'Flujo de fondos',
    link: '/panel/reportes/flujo-fondos',
    icon: <FaExchangeAlt />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'reports',
    activePrefix: '/panel/reportes/flujo-fondos',
    collapsedHidden: true,
  },
  {
    key: 'nav_report_morosos',
    title: 'Morosos',
    link: '/panel/reportes/morosos',
    icon: <FaExclamationTriangle />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'reports',
    activePrefix: ['/panel/reportes/morosos', '/panel/notificaciones/morosos'],
    collapsedHidden: true,
  },
  {
    key: 'nav_configuration',
    title: 'Configuración',
    link: '/panel/configuración',
    icon: <FaCog />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    capabilityKey: 'nav_configuration',
    activePrefix: ['/panel/configuración', '/panel/configuracion', '/panel/acceso_terceros', '/panel/afip-wizard'],
  },
  {
    key: 'nav_current_accounts',
    title: 'Cuentas corrientes',
    link: '/panel/ctas_ctes',
    icon: <FaLayerGroup />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    activePrefix: '/panel/ctas_ctes',
  },
  {
    key: 'nav_consortiums',
    title: 'Consorcios',
    link: '/panel/configuración/listado/consorcios',
    icon: <FaLayerGroup />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    activePrefix: '/panel/configuración/listado/consorcios',
    collapsedHidden: true,
  },
  {
    key: 'nav_owners',
    title: 'Propietarios',
    link: '/panel/configuración/listado/propietarios',
    icon: <FaUserTie />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    activePrefix: '/panel/configuración/listado/propietarios',
    collapsedHidden: true,
  },
  {
    key: 'nav_import_owners',
    title: 'Importar propietarios',
    link: '/panel/configuración/importar/propietarios',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    activePrefix: '/panel/configuración/importar/propietarios',
    collapsedHidden: true,
  },
  {
    key: 'nav_users',
    title: 'Usuarios',
    link: '/panel/configuración/listado/usuarios',
    icon: <FaUserTie />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    activePrefix: '/panel/configuración/listado/usuarios',
    collapsedHidden: true,
  },
  {
    key: 'nav_admins',
    title: 'Administradores',
    link: '/panel/configuración/listado/administradores',
    icon: <FaUserTie />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    activePrefix: ['/panel/configuración/listado/administradores', '/panel/configuracion/listado/administradores'],
    collapsedHidden: true,
  },
  {
    key: 'nav_periods',
    title: 'Períodos',
    link: '/panel/configuración/listado/periodos',
    icon: <FaCalendarAlt />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    activePrefix: '/panel/configuración/listado/periodos',
    collapsedHidden: true,
  },
  {
    key: 'nav_headers',
    title: 'Encabezado / branding',
    link: '/panel/configuración/encabezados',
    icon: <FaCog />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    activePrefix: ['/panel/configuración/encabezados', '/panel/configuración/encabezado'],
    collapsedHidden: true,
  },
  {
    key: 'nav_third_party_access',
    title: 'Accesos terceros',
    link: '/panel/acceso_terceros/listar/',
    icon: <FaLock />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    activePrefix: '/panel/acceso_terceros',
    collapsedHidden: true,
  },
  {
    key: 'nav_manual',
    title: 'Manual',
    link: '/panel/manual-de-procedimiento',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    activePrefix: [
      '/panel/manual-de-procedimiento',
      '/panel/configuración/manual/videos',
      '/panel/configuracion/manual/videos',
      '/panel/manual/instructional-videos',
    ],
    collapsedHidden: true,
  },
  {
    key: 'nav_novedades_wam',
    title: 'Novedades de WAM',
    link: '/panel/novedades-wam',
    icon: <FaBullhorn />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    activePrefix: ['/panel/novedades-wam'],
    collapsedHidden: true,
  },
  {
    key: 'nav_videos',
    title: 'Videos',
    link: '/panel/configuración/manual/videos',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    activePrefix: ['/panel/configuración/manual/videos', '/panel/configuracion/manual/videos'],
    collapsedHidden: true,
  },
  {
    key: 'nav_afip',
    title: 'AFIP',
    link: '/panel/afip-wizard',
    icon: <FaReceipt />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'administration',
    activePrefix: '/panel/afip-wizard',
    collapsedHidden: true,
  },
  {
    key: 'nav_audit',
    title: 'Auditoría',
    link: '/panel/auditoria',
    icon: <FaUserShield />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'audit',
    capabilityKey: 'nav_audit',
    activePrefix: ['/panel/auditoria', '/panel/legales', '/reportes/propietarios', '/panel/servicios/cumplimiento-laboral-caba'],
  },
  {
    key: 'nav_legales',
    title: 'Legales',
    link: '/panel/legales',
    icon: <FaBalanceScale />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'audit',
    activePrefix: '/panel/legales',
    collapsedHidden: true,
  },
  {
    key: 'nav_owner_certificate',
    title: 'Reporte propietarios',
    link: '/reportes/propietarios',
    icon: <FaClipboardList />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'audit',
    activePrefix: '/reportes/propietarios',
    collapsedHidden: true,
  },
  {
    key: 'nav_closing_checklist',
    title: 'Checklist de cierre',
    link: '/panel/autopilot',
    icon: <FaRobot />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'audit',
    capabilityKey: 'nav_audit',
    activePrefix: '/panel/autopilot',
    collapsedHidden: true,
  },
  {
    key: 'nav_compliance',
    title: 'Cumplimiento laboral',
    link: '/panel/servicios/cumplimiento-laboral-caba',
    icon: <FaUserShield />,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'audit',
    activePrefix: '/panel/servicios/cumplimiento-laboral-caba',
    collapsedHidden: true,
  },
  {
    key: 'nav_backoffice',
    title: 'Backoffice',
    link: '/panel/super/backoffice',
    icon: <FaUserShield />,
    onlySuperUser: true,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'backoffice',
    activePrefix: ['/panel/super/backoffice', '/panel/super/dashboard', '/panel/active-users-uf'],
  },
  {
    key: 'nav_backoffice_console',
    title: 'Superuser console',
    link: '/panel/super/dashboard',
    icon: <FaTachometerAlt />,
    onlySuperUser: true,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'backoffice',
    activePrefix: '/panel/super/dashboard',
    collapsedHidden: true,
  },
  {
    key: 'nav_asistente',
    title: 'Asistente',
    link: '/panel/asistente',
    icon: <FaRobot />,
    // Solo si el backend habilita el asistente para la administración (estado del AsistenteProvider).
    onlyAsistente: true,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'principal',
    activePrefix: '/panel/asistente',
  },
  {
    key: 'nav_backoffice_asistente',
    title: 'Asistente (piloto)',
    link: '/panel/super/asistente',
    icon: <FaRobot />,
    onlySuperUser: true,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'backoffice',
    activePrefix: '/panel/super/asistente',
    collapsedHidden: true,
  },
  {
    key: 'nav_backoffice_active_admins',
    title: 'Active admins',
    link: '/panel/active-users-uf',
    icon: <FaUserShield />,
    onlySuperUser: true,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'backoffice',
    activePrefix: '/panel/active-users-uf',
    collapsedHidden: true,
  },
  {
    key: 'nav_backoffice_pricing',
    title: 'Billing pricing',
    link: '/panel/super/backoffice/billing/pricing',
    icon: <FaCoins />,
    onlySuperUser: true,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'backoffice',
    activePrefix: '/panel/super/backoffice/billing/pricing',
    collapsedHidden: true,
  },
  {
    key: 'nav_backoffice_generate',
    title: 'Billing generate',
    link: '/panel/super/backoffice/billing/generate',
    icon: <FaClipboardList />,
    onlySuperUser: true,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'backoffice',
    activePrefix: '/panel/super/backoffice/billing/generate',
    collapsedHidden: true,
  },
  {
    key: 'nav_backoffice_invoices',
    title: 'Invoices',
    link: '/panel/super/backoffice/billing/invoices',
    icon: <FaFileInvoiceDollar />,
    onlySuperUser: true,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'backoffice',
    activePrefix: '/panel/super/backoffice/billing/invoices',
    collapsedHidden: true,
  },
  {
    key: 'nav_backoffice_ledger',
    title: 'Ledger',
    link: '/panel/super/backoffice/billing/ledger',
    icon: <FaBalanceScale />,
    onlySuperUser: true,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'backoffice',
    activePrefix: '/panel/super/backoffice/billing/ledger',
    collapsedHidden: true,
  },
  {
    key: 'nav_backoffice_expenses',
    title: 'Super expenses',
    link: '/panel/super/backoffice/billing/expenses',
    icon: <FaFileInvoiceDollar />,
    onlySuperUser: true,
    section: UI_CONTEXTS.CONSORCIOS,
    group: 'backoffice',
    activePrefix: ['/panel/super/backoffice/billing/expenses', '/panel/super/backoffice/billing/gastos'],
    collapsedHidden: true,
  },
  // Mi Administración
  {
    key: 'contab_admin',
    title: 'Contabilidad',
    link: '/contabilidad-admin',
    // Herramientas del Modo contador: no tienen ítem propio en la barra lateral.
    contabTabs: ['plan', 'asientos', 'mayor', 'balance', 'apertura', 'periodos', 'reportes', 'auditoria'],
    icon: <FaTachometerAlt />,
    addonCode: 'CONTAB_ADMIN',
    section: UI_CONTEXTS.MI_ADMIN,
    group: 'mi_admin',
    children: [
      // Los 5 lugares del módulo; las herramientas técnicas se abren con "Modo contador".
      // contabTabs: pestañas que se muestran con ese título en el encabezado.
      { title: 'Inicio', link: '/contabilidad-admin?tab=dashboard', icon: FaTachometerAlt },
      {
        title: 'Movimientos',
        link: '/contabilidad-admin?tab=ventas',
        icon: FaExchangeAlt,
        contabTabs: ['ventas', 'cobranzas', 'compras', 'pagos', 'sueldos'],
      },
      { title: 'Me deben / Debo', link: '/contabilidad-admin?tab=ctacte', icon: FaLayerGroup },
      { title: 'Banco', link: '/contabilidad-admin?tab=conciliacion', icon: FaMoneyBillWave },
      {
        title: 'Impuestos',
        link: '/contabilidad-admin?tab=impuestos',
        icon: FaReceipt,
        contabTabs: ['impuestos', 'iva', 'iibb', 'ganancias_pf'],
      },
      { title: 'Configuración', link: '/contabilidad-admin?tab=config', icon: FaCog },
    ],
  },
];

const QUICK_ACTIONS = [
  { label: 'Nuevo Gasto', path: '/panel/movimientos/gastos/agregar/gasto' },
  { label: 'Nueva Cobranza', path: '/panel/movimientos/cobranzas/agregar/cobranza' },
  { label: 'Nuevo Reclamo', path: '/panel/servicios/reclamos' },
  { label: 'Nueva Liquidación', path: '/panel/liquidacion/agregar/liquidacion-expensa' },
];

const PRO_NOTIFICATION_TEMPLATE = { total: 0, operativas: 0, sistema: 0, mensajes: 0 };

/* ================= IconPill (botón pill del header) ================= */
function IconPill({ label, icon: Icon, tint = 'gray', badge = 0, onClick }) {
  const base   = useColorModeValue('whiteAlpha.300', 'whiteAlpha.100');
  const hover  = useColorModeValue('whiteAlpha.500', 'whiteAlpha.200');
  const bColor = useColorModeValue('blackAlpha.200', 'whiteAlpha.200');
  const col    = useColorModeValue(`${tint}.600`, `${tint}.300`);
  const colH   = useColorModeValue(`${tint}.700`, `${tint}.200`);
  return (
    <Box position="relative">
      <Tooltip hasArrow label={label} placement="bottom" openDelay={250}>
        <IconButton
          aria-label={label}
          icon={<Icon />}
          onClick={onClick}
          size="md"
          variant="ghost"
          rounded="2xl"
          color={col}
          bg={base}
          borderWidth="1px"
          borderColor={bColor}
          _hover={{ bg: hover, color: colH, transform: 'translateY(-1px)', boxShadow: 'md' }}
          _active={{ transform: 'translateY(0) scale(.98)' }}
        />
      </Tooltip>
      {badge > 0 && (
        <Box
          position="absolute"
          top="-4px"
          right="-4px"
          bg={`${tint}.500`}
          color="white"
          minW="18px"
          h="18px"
          px="1"
          borderRadius="full"
          fontSize="10px"
          fontWeight="bold"
          display="flex"
          alignItems="center"
          justifyContent="center"
          lineHeight="18px"
          boxShadow="0 0 0 2px rgba(255,255,255,0.85)"
        >
          {badge > 99 ? '99+' : badge}
        </Box>
      )}
    </Box>
  );
}

/* ================= ActivityDock (drawer de notificaciones) ================= */
function ActivityDock({ isOpen, onClose, notifications = [], counts = {}, onRead, onReadAll, onClearAll, onNavigate }) {
  const [tab, setTab] = useState(0);
  const [q, setQ]     = useState('');
  const bg     = useColorModeValue('whiteAlpha.95', 'rgba(16,18,27,.92)');
  const bColor = useColorModeValue('blackAlpha.200', 'whiteAlpha.200');
  const dotBg  = useColorModeValue('whiteAlpha.900', 'blackAlpha.400');

  const typeMeta = useMemo(() => ({
    'bot-cobranza': { label: 'Cobranza', color: 'green',  Icon: FaMoneyBillWave },
    'bot-reclamo' : { label: 'Reclamo',  color: 'yellow', Icon: FaExclamationTriangle },
    'bot-reserva' : { label: 'Reserva',  color: 'blue',   Icon: FaCalendarCheck },
    'zip'         : { label: 'Archivo',  color: 'cyan',   Icon: FaClipboardList },
    'zip-error'   : { label: 'Error ZIP',color: 'red',    Icon: FaTimesCircle },
    'notify'      : { label: 'Aviso',    color: 'gray',   Icon: FaClipboardList },
  }), []);

  const filterByTab = (arr) =>
    tab === 1 ? arr.filter(n => n.type === 'bot-cobranza')
    : tab === 2 ? arr.filter(n => n.type === 'bot-reclamo')
    : tab === 3 ? arr.filter(n => n.type === 'bot-reserva')
    : arr;

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const base = filterByTab(notifications);
    return t ? base.filter(n => (n?.message || '').toLowerCase().includes(t)) : base;
  }, [q, notifications, tab]);

  const Card = ({ n }) => {
    const meta = typeMeta[n?.type] || typeMeta['notify'];
    const Icon = meta.Icon;
    const tint = meta.color;
    const unread = !n?.read;
    return (
      <HStack align="flex-start" spacing={4} px={3} py={3} borderWidth="1px" borderRadius="xl" borderColor={bColor}
              bg={useColorModeValue('blackAlpha.50','whiteAlpha.50')} _hover={{ boxShadow:'md' }}>
        <Box pt={1}>
          <Box w="12px" h="12px" borderRadius="full" bg={`${tint}.400`} borderWidth="2px" borderColor={dotBg}/>
        </Box>
        <VStack align="stretch" spacing={1} flex={1}>
          <HStack spacing={2}>
            <Tag size="sm" colorScheme={tint} variant="subtle"><TagLeftIcon as={Icon} />{meta.label}</Tag>
            {unread && <Badge colorScheme={tint}>Nuevo</Badge>}
          </HStack>
          <Text fontSize="sm" noOfLines={4}>{n?.message}</Text>
          <HStack spacing={2} pt={1}>
            {unread && <Button size="xs" leftIcon={<FaCheck />} onClick={() => onRead?.(n.id)}>Marcar leído</Button>}
            {n?.link && (
              <Button size="xs" variant="ghost" rightIcon={<FaExternalLinkAlt />} onClick={() => onNavigate?.(n.link)}>
                Abrir
              </Button>
            )}
          </HStack>
        </VStack>
      </HStack>
    );
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent bg={bg} backdropFilter="blur(10px) saturate(1.1)">
        <DrawerCloseButton />
        <DrawerHeader>
          <HStack justify="space-between">
            <Text fontWeight="extrabold">Centro de actividad</Text>
            <HStack>
              <Badge colorScheme="green">Cobranza: {counts.cobranzas || 0}</Badge>
              <Badge colorScheme="yellow">Reclamos: {counts.reclamos || 0}</Badge>
              <Badge colorScheme="blue">Reservas: {counts.reservas || 0}</Badge>
            </HStack>
          </HStack>
        </DrawerHeader>
        <DrawerBody>
          <VStack align="stretch" spacing={4}>
            <InputGroup>
              <InputLeftElement pointerEvents="none"><FaSearch /></InputLeftElement>
              <Input placeholder="Buscar en actividad…" value={q} onChange={(e) => setQ(e.target.value)} borderRadius="lg"/>
            </InputGroup>

            <Tabs index={tab} onChange={setTab} variant="soft-rounded" colorScheme="blue">
              <TabList>
                <Tab>Todos</Tab><Tab>Cobranzas</Tab><Tab>Reclamos</Tab><Tab>Reservas</Tab>
              </TabList>
              <TabPanels pt={2}>
                {[0,1,2,3].map((i) => (
                  <TabPanel key={i} px={0}>
                    <VStack align="stretch" spacing={3}>
                      {filterByTab(filtered).length === 0
                        ? <Box p={6} textAlign="center" borderWidth="1px" borderRadius="lg" borderColor={bColor}>
                            <Text fontSize="sm" color="gray.500">No hay actividad para mostrar.</Text>
                          </Box>
                        : filterByTab(filtered).map((n) => <Card key={n.id || Math.random()} n={n} />)
                      }
                    </VStack>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>

            <Divider />
            <HStack justify="space-between">
              <Button size="sm" onClick={() => onReadAll?.()} leftIcon={<FaCheck />}>Marcar todo como leído</Button>
              <Button size="sm" variant="ghost" onClick={() => onClearAll?.()} leftIcon={<FaTrash />}>Limpiar lista</Button>
            </HStack>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

/* --------------------------- LAYOUT PRINCIPAL --------------------------- */
export default function LayoutWAM({ children }) {
  const {
    useInfoUserAuth,
    addonsStatus = { isLoading: true, data: {}, fetchedAt: null },
    refreshAddonStatus,
    capabilities,
  } =
    useContext(UseDataUserContext);
  const user      = useInfoUserAuth?.data || {};
  const isSuper   = !!user?.is_superuser;
  const authToken = String(user?.token || '');
  const userId    = user?.id || null;
  const headerV2Flag = process.env.REACT_APP_HEADER_V2;
  const headerV2ProFlag = process.env.REACT_APP_HEADER_V2_PRO;
  const useHeaderV2 = headerV2Flag === undefined ? true : headerV2Flag === "true";
  const useHeaderV2Pro = useHeaderV2 && (headerV2ProFlag === undefined ? true : headerV2ProFlag === "true");
  const addonStatusMap = addonsStatus?.data || {};
  const addonsLoading = addonsStatus?.isLoading;
  const collapsedSidebarWidthValue = useBreakpointValue(SIDEBAR_COL_W) || 0;
  const expandedSidebarWidthValue = useBreakpointValue(SIDEBAR_EXP_W) || collapsedSidebarWidthValue;
  const headerHeightValue = useBreakpointValue(HEADER_HEIGHT) || 64;
  const contentMaxWidth = useBreakpointValue(CONTENT_MAX_W) || '100%';

  const getAddonLockMeta = useCallback(
    (addonCode) => {
      if (!addonCode || addonsLoading) {
        return { locked: false, code: null, message: null };
      }
      const meta = addonStatusMap[addonCode];
      if (!meta || !meta.is_active) {
        return {
          locked: true,
          code: 'inactive',
          message: 'Módulo no habilitado. Activá tu plan para acceder.',
        };
      }
      if (Object.prototype.hasOwnProperty.call(meta, 'access_allowed') && meta.access_allowed === false) {
        return {
          locked: true,
          code: 'no_access',
          message: 'No tenés permisos para acceder a Mi Administración. Pedile al titular que habilite tu usuario.',
        };
      }
      return { locked: false, code: null, message: null };
    },
    [addonStatusMap, addonsLoading]
  );

  const contabAddonLockMeta = useMemo(() => getAddonLockMeta('CONTAB_ADMIN'), [getAddonLockMeta]);
  const contabAddonActive = !contabAddonLockMeta.locked;
  const GATED_ROUTE_PREFIXES = useMemo(() => ['/contabilidad-admin', '/administracion'], []);

  const CONTEXT_STORAGE_KEY = 'wam_context';
  const getInitialContext = () => {
    if (typeof window === 'undefined') return UI_CONTEXTS.CONSORCIOS;
    const stored = window.localStorage.getItem(CONTEXT_STORAGE_KEY);
    return stored === UI_CONTEXTS.MI_ADMIN ? UI_CONTEXTS.MI_ADMIN : UI_CONTEXTS.CONSORCIOS;
  };
  const [uiContext, setUiContextState] = useState(getInitialContext);

  const updateUiContext = useCallback(
    (next) => {
      if (!next) return;
      setUiContextState((prev) => {
        if (prev !== next) {
          traceWamNav('UI_CONTEXT_STATE_SET', {
            previous: prev,
            next,
            pathname: typeof window !== 'undefined' ? window.location.pathname : '',
            search: typeof window !== 'undefined' ? window.location.search : '',
            storedContext:
              typeof window !== 'undefined'
                ? window.localStorage.getItem(CONTEXT_STORAGE_KEY)
                : null,
          });
        }
        return prev === next ? prev : next;
      });
    },
    []
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(CONTEXT_STORAGE_KEY, uiContext);
      traceWamNav('UI_CONTEXT_PERSISTED', {
        uiContext,
        pathname: window.location.pathname,
        search: window.location.search,
        storedContext: window.localStorage.getItem(CONTEXT_STORAGE_KEY),
      });
    }
  }, [uiContext]);

  const navigate  = useNavigate();
  const location  = useLocation();
  const toast     = useToast();
  const isCorreoRoute = location.pathname.startsWith('/panel/correo');
  const effectiveContentMaxWidth = isCorreoRoute ? '100%' : contentMaxWidth;
  const latestUiContextRef = useRef(uiContext);
  const latestLocationRef = useRef({ pathname: location.pathname, search: location.search });
  const lastConsorciosRouteRef = useRef('/');
  const contextTransitionTargetRef = useRef(null);
  const pendingNavRef = useRef(false);
  const primeUiContextTransition = useCallback(
    (next) => {
      if (!next) return;
      latestUiContextRef.current = next;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(CONTEXT_STORAGE_KEY, next);
      }
      traceWamNav('UI_CONTEXT_PRIMED', {
        next,
        pathname: typeof window !== 'undefined' ? window.location.pathname : location.pathname,
        search: typeof window !== 'undefined' ? window.location.search : location.search,
        storedContext:
          typeof window !== 'undefined'
            ? window.localStorage.getItem(CONTEXT_STORAGE_KEY)
            : null,
      });
    },
    [location.pathname, location.search]
  );
  const markNavPending = useCallback(() => {
    traceWamNav('NAV_PENDING_MARK', {
      pathname: typeof window !== 'undefined' ? window.location.pathname : '',
      search: typeof window !== 'undefined' ? window.location.search : '',
      uiContext,
    });
    pendingNavRef.current = true;
    window.setTimeout(() => {
      pendingNavRef.current = false;
      traceWamNav('NAV_PENDING_CLEAR', {
        pathname: typeof window !== 'undefined' ? window.location.pathname : '',
        search: typeof window !== 'undefined' ? window.location.search : '',
        uiContext:
          typeof window !== 'undefined'
            ? window.localStorage.getItem(CONTEXT_STORAGE_KEY)
            : uiContext,
      });
    }, 400);
  }, [uiContext]);

  useLayoutEffect(() => {
    latestUiContextRef.current = uiContext;
  }, [uiContext]);

  useLayoutEffect(() => {
    latestLocationRef.current = {
      pathname: location.pathname,
      search: location.search,
    };
  }, [location.pathname, location.search]);

  const isContabRoute = location.pathname.startsWith('/contabilidad-admin');
  const contabActiveTab = useMemo(() => {
    if (!isContabRoute) return null;
    try {
      const params = new URLSearchParams(location.search);
      const slug = (params.get('tab') || 'dashboard').toLowerCase();
      return normalizeContabTab(slug);
    } catch {
      return 'dashboard';
    }
  }, [isContabRoute, location.search]);
  const taxTabs = ['impuestos', 'iva', 'iibb', 'ganancias_pf'];
  const isTaxTab = taxTabs.includes(contabActiveTab || '');

  useEffect(() => {
    if (!isConsorciosRoutePath(location.pathname)) return;
    const nextRoute = `${location.pathname}${location.search || ''}`;
    lastConsorciosRouteRef.current = nextRoute;
    if (contextTransitionTargetRef.current === UI_CONTEXTS.CONSORCIOS) {
      traceWamNav('CONSORCIOS_TRANSITION_RESOLVED', {
        pathname: location.pathname,
        search: location.search,
        uiContext,
      });
      contextTransitionTargetRef.current = null;
    }
    traceWamNav('LAST_CONSORCIOS_ROUTE_UPDATED', {
      pathname: location.pathname,
      search: location.search,
      storedRoute: nextRoute,
      uiContext,
    });
  }, [location.pathname, location.search, uiContext]);

  useEffect(() => {
    if (!isContabRoute) return;
    if (contextTransitionTargetRef.current === UI_CONTEXTS.MI_ADMIN) {
      traceWamNav('MI_ADMIN_TRANSITION_RESOLVED', {
        pathname: location.pathname,
        search: location.search,
        uiContext,
      });
      contextTransitionTargetRef.current = null;
    }
  }, [isContabRoute, location.pathname, location.search, uiContext]);

  useEffect(() => {
    traceWamNav('LAYOUT_LOCATION_CHANGED', {
      pathname: location.pathname,
      search: location.search,
      uiContext,
      storedContext:
        typeof window !== 'undefined'
          ? window.localStorage.getItem(CONTEXT_STORAGE_KEY)
          : null,
    });
  }, [location.pathname, location.search, uiContext]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const detail = event.detail || {};
      const notification = detail.notification || detail;
      const title = notification?.title || "Notificación nueva";
      const description = notification?.body || detail?.body || undefined;
      toast({
        title,
        description,
        status: "info",
        duration: 4000,
        isClosable: true,
      });
    };
    window.addEventListener("push:received", handler);
    return () => window.removeEventListener("push:received", handler);
  }, [toast]);

  useEffect(() => {
    if (pendingNavRef.current) {
      traceWamNav('SYNC_FROM_PATH_SKIPPED_PENDING', {
        pathname: location.pathname,
        search: location.search,
        uiContext,
      });
      return;
    }
    const leavingToConsorcios = contextTransitionTargetRef.current === UI_CONTEXTS.CONSORCIOS;
    const storedContext =
      typeof window !== 'undefined'
        ? window.localStorage.getItem(CONTEXT_STORAGE_KEY)
        : null;
    const selectorIntendsConsorcios = uiContext === UI_CONTEXTS.CONSORCIOS || storedContext === UI_CONTEXTS.CONSORCIOS;
    if (isContabRoute && uiContext !== UI_CONTEXTS.MI_ADMIN && !leavingToConsorcios && !selectorIntendsConsorcios) {
      traceWamNav('SYNC_FROM_PATH_TO_MI_ADMIN', {
        pathname: location.pathname,
        search: location.search,
        uiContextBefore: uiContext,
        transitionTarget: contextTransitionTargetRef.current,
        storedContext,
      });
      updateUiContext(UI_CONTEXTS.MI_ADMIN);
      return;
    }
    traceWamNav('SYNC_FROM_PATH_TO_MI_ADMIN_IDLE', {
      pathname: location.pathname,
      search: location.search,
      uiContext,
      transitionTarget: contextTransitionTargetRef.current,
      storedContext,
      selectorIntendsConsorcios,
    });
  }, [isContabRoute, location.pathname, location.search, uiContext, updateUiContext]);

  useEffect(() => {
    if (pendingNavRef.current) {
      traceWamNav('SYNC_FROM_CONSORCIOS_PATH_SKIPPED_PENDING', {
        pathname: location.pathname,
        search: location.search,
        uiContext,
      });
      return;
    }
    if (isConsorciosRoutePath(location.pathname) && uiContext !== UI_CONTEXTS.CONSORCIOS) {
      traceWamNav('SYNC_FROM_PATH_TO_CONSORCIOS', {
        pathname: location.pathname,
        search: location.search,
        uiContextBefore: uiContext,
      });
      updateUiContext(UI_CONTEXTS.CONSORCIOS);
    }
  }, [location.pathname, location.search, uiContext, updateUiContext]);

  useEffect(() => {
    if (pendingNavRef.current) {
      traceWamNav('SYNC_NAVIGATION_SKIPPED_PENDING', {
        pathname: location.pathname,
        search: location.search,
        uiContext,
      });
      return;
    }
    if (uiContext === UI_CONTEXTS.MI_ADMIN) {
      const currentRouteIsConsorcios = isConsorciosRoutePath(location.pathname);
      if (!isContabRoute && !currentRouteIsConsorcios) {
        traceWamNav('SYNC_NAVIGATE_TO_MI_ADMIN', {
          fromPathname: location.pathname,
          fromSearch: location.search,
          uiContext,
          currentRouteIsConsorcios,
          storedContext:
            typeof window !== 'undefined'
              ? window.localStorage.getItem(CONTEXT_STORAGE_KEY)
              : null,
          destination: '/contabilidad-admin?tab=dashboard',
        });
        navigate('/contabilidad-admin?tab=dashboard');
      }
    } else if (uiContext === UI_CONTEXTS.CONSORCIOS) {
      const storedContext =
        typeof window !== 'undefined'
          ? window.localStorage.getItem(CONTEXT_STORAGE_KEY)
          : null;
      const shouldRestoreConsorciosRoute =
        isContabRoute &&
        (
          contextTransitionTargetRef.current === UI_CONTEXTS.CONSORCIOS ||
          storedContext === UI_CONTEXTS.CONSORCIOS
        );
      if (shouldRestoreConsorciosRoute) {
        const consorciosDestination = lastConsorciosRouteRef.current || '/';
        traceWamNav('SYNC_NAVIGATE_TO_CONSORCIOS', {
          fromPathname: location.pathname,
          fromSearch: location.search,
          uiContext,
          transitionTarget: contextTransitionTargetRef.current,
          storedContext,
          destination: consorciosDestination,
        });
        markNavPending();
        navigate(consorciosDestination);
      } else {
        traceWamNav('SYNC_NAVIGATE_TO_CONSORCIOS_IDLE', {
          fromPathname: location.pathname,
          fromSearch: location.search,
          uiContext,
          transitionTarget: contextTransitionTargetRef.current,
          storedContext,
        });
      }
    }
  }, [uiContext, isContabRoute, location.pathname, location.search, markNavPending, navigate]);

  const sidebarDrawer   = useDisclosure();  // Drawer mobile

  const handleNavClick = useCallback(
    (sectionKey, closeDrawer = false) => {
      const nextSection = sectionKey || UI_CONTEXTS.CONSORCIOS;
      if (nextSection !== uiContext) {
        traceWamNav('SIDEBAR_NAV_CONTEXT_SET', {
          previous: uiContext,
          next: nextSection,
          pathname: location.pathname,
          search: location.search,
        });
        contextTransitionTargetRef.current = nextSection;
        primeUiContextTransition(nextSection);
      }
      updateUiContext(nextSection);
      if (closeDrawer) {
        sidebarDrawer.onClose();
      }
    },
    [location.pathname, location.search, primeUiContextTransition, sidebarDrawer, uiContext, updateUiContext]
  );
  const dock      = useDisclosure();  // Activity dock
  const { colorMode, toggleColorMode } = useColorMode();

  const headerBg  = useColorModeValue('rgba(22, 74, 170, 0.9)', 'rgba(10, 21, 50, 0.9)');
  const mainBg = useColorModeValue('gray.50', 'gray.900');
  const bodyBgColor = useColorModeValue('#f8fafc', '#050912');
  const responsiveBorderColor = useColorModeValue('rgba(15,23,42,0.08)', 'rgba(255,255,255,0.16)');
  const responsiveBackground = useColorModeValue('#ffffff', 'rgba(10, 15, 28, 0.92)');
  const responsiveShadow = useColorModeValue(
    'inset 0 1px 0 rgba(15,23,42,0.04)',
    'inset 0 1px 0 rgba(255,255,255,0.08)'
  );
  const scrollbarThumb = useColorModeValue('rgba(15,23,42,0.2)', 'rgba(255,255,255,0.25)');
  // Fallback sólido para la columna sticky global (ver globalStyles más abajo).
  // Mismo par de valores que ya usan las tablas que arman su propio sticky a
  // mano (GastosTableSticky, PorcentajeUnidadList, InformeAnualReport,
  // CommunicationDispatchControlPage: todas usan useColorModeValue("white", "gray.800")).
  // Se resuelven con useToken porque globalStyles se inyecta con <Global> de emotion,
  // que emite CSS crudo: un token de Chakra como "gray.800" NO es CSS válido y el
  // navegador descartaba la declaración, dejando la celda transparente en modo oscuro.
  const [stickyColumnBgLight, stickyColumnBgDark] = useToken('colors', ['white', 'gray.800']);
  const stickyColumnBg = useColorModeValue(stickyColumnBgLight, stickyColumnBgDark);
  const addonWarningRef = useRef(0);
  const showAddonLockToast = useCallback(
    (lockMeta) => {
      if (!lockMeta?.locked) {
        return;
      }
      const nowTs = Date.now();
      if (nowTs - addonWarningRef.current < 1200) {
        return;
      }
      const isPermissionIssue = lockMeta.code === 'no_access';
      toast({
        title: isPermissionIssue ? 'Sin permisos para Mi Administración' : 'Módulo no habilitado',
        description:
          lockMeta.message ||
          (isPermissionIssue
            ? 'Pedile al titular que habilite tu usuario.'
            : 'Activá el módulo para acceder.'),
        status: 'warning',
        duration: 4000,
      });
      addonWarningRef.current = nowTs;
    },
    [toast]
  );

  const [legacySidebarCollapsed, setLegacySidebarCollapsed] = useState(true);
  const [openGroups, setOpenGroups] = useState({});
  const sidebarWidthLegacy = legacySidebarCollapsed ? collapsedSidebarWidthValue : expandedSidebarWidthValue;

  const [online, setOnline] = useState(typeof window !== 'undefined' ? navigator.onLine : true);
  const [now, setNow]       = useState(new Date());
  const handleConsorcioChange = useCallback((consorcio) => {
    setSelectedConsorcio(consorcio || null);
  }, []);

  const handleLogout = async () => {
    try {
      // Logout only clears auth session. Push token/topic stays active by design.
      await clearAuthStorage();
    } catch (error) {
      console.warn('[auth] clearAuthStorage failed on logout', error);
    }
    window.location.href = '/';
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const searchDebounceRef = useRef(null);
  const latestSearchRef = useRef(0);

  const clearSearchState = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setSearchResults(null);
    setSearchLoading(false);
    setSearchError(null);
  }, []);

  const performGlobalSearch = useCallback(async (term) => {
    const trimmed = term.trim();
    if (trimmed.length < 2) return;
    const requestId = Date.now();
    latestSearchRef.current = requestId;
    setSearchLoading(true);
    setSearchError(null);
    const res = await ApiConsume('META__GLOBAL_SEARCH', 'GET', {}, null, '', false, { q: trimmed });
    if (latestSearchRef.current !== requestId) {
      return;
    }
    if (res.status >= 200 && res.status < 400) {
      setSearchResults(res.data || {});
      setSearchError(null);
    } else {
      setSearchResults(null);
      setSearchError(res.msg_error || 'No se pudo completar la búsqueda.');
    }
    setSearchLoading(false);
  }, []);

  const handleSearchQueryChange = useCallback((value) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    const trimmed = (value || '').trim();
    if (trimmed.length < 2) {
      clearSearchState();
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      performGlobalSearch(trimmed);
    }, 300);
  }, [clearSearchState, performGlobalSearch]);

  const runSearchNow = useCallback(() => {
    const trimmed = (searchQuery || '').trim();
    if (trimmed.length < 2) {
      clearSearchState();
      return;
    }
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    performGlobalSearch(trimmed);
  }, [clearSearchState, performGlobalSearch, searchQuery]);

  const handleSearchNavigate = useCallback((url) => {
    if (!url) return;
    if (/^https?:\/\//i.test(url)) {
      openUrl(url);
    } else {
      navigate(url);
    }
    setSearchQuery('');
    clearSearchState();
  }, [clearSearchState, navigate]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, []);

  /* reloj + online */
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    const up = () => setOnline(true), dn = () => setOnline(false);
    window.addEventListener('online', up); window.addEventListener('offline', dn);
    return () => { clearInterval(t); window.removeEventListener('online', up); window.removeEventListener('offline', dn); };
  }, []);

  useEffect(() => {
    if (useHeaderV2Pro) {
      return;
    }
    setProNotifications([]);
    setProNotificationCounts({ ...PRO_NOTIFICATION_TEMPLATE });
    setAsyncJobs([]);
    setWeatherMeta(null);
    setQuickBalance(null);
    setSelectedConsorcio(null);
  }, [useHeaderV2Pro]);

  /* notificaciones + contadores */
  const [notifications, setNotifications] = useState([]);
  const unread = notifications.filter(n => !n.read).length;
  const [counts, setCounts] = useState({ cobranzas: 0, reclamos: 0, reservas: 0 });
  const [proNotifications, setProNotifications] = useState([]);
  const [proNotificationCounts, setProNotificationCounts] = useState({ ...PRO_NOTIFICATION_TEMPLATE });
  const [asyncJobs, setAsyncJobs] = useState([]);
  const [weatherMeta, setWeatherMeta] = useState(null);
  const [quickBalance, setQuickBalance] = useState(null);
  const [selectedConsorcio, setSelectedConsorcio] = useState(null);
  const [consorciosLista, setConsorciosLista] = useState([]);
  const selectedConsorcioId = selectedConsorcio?.id || null;
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refreshProNotifications = useCallback(async () => {
    if (!useHeaderV2Pro) return;
    try {
      const res = await ApiConsume('META__NOTIFICATIONS');
      if (res.status >= 200 && res.status < 400) {
        if (!isMountedRef.current) return;
        setProNotifications(res.data?.items || []);
        setProNotificationCounts({
          ...PRO_NOTIFICATION_TEMPLATE,
          ...(res.data?.counts || {}),
        });
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('Notifications summary failed', error);
      }
    }
  }, [useHeaderV2Pro]);

  /* persistencia de contadores */
  useEffect(() => {
    try {
      const k = `WAM_ACTIVITY_COUNTS:${userId || 'anon'}`;
      const raw = localStorage.getItem(k);
      if (raw) setCounts({ cobranzas:0, reclamos:0, reservas:0, ...JSON.parse(raw) });
    } catch {}
  }, [userId]);
  useEffect(() => {
    try { localStorage.setItem(`WAM_ACTIVITY_COUNTS:${userId || 'anon'}`, JSON.stringify(counts)); } catch {}
  }, [counts, userId]);

  const pushNotification = (n) => {
    const ok = new Set(['zip','zip-error','notify','bot-cobranza','bot-reclamo','bot-reserva']);
    if (!ok.has(n?.type)) return;
    setNotifications(prev => prev.some(p => p.id === n.id) ? prev : [n, ...prev]);
  };

  /* ZIP / avisos */
  useEffect(() => {
    const onZipFinished = (e) => {
      const { download_url, informeId, ufs } = e.detail || {};
      pushNotification({
        id: `zip-finished-${informeId || 'na'}-${Date.now()}`, type: 'zip',
        message: `ZIP listo${informeId ? ` (Informe #${informeId})` : ''}${ufs?.length ? ` — ${ufs.length} UFs` : ''}`,
        link: download_url, read: false, icon: <FaCheckCircle color="#16a34a" />,
      });
      toast({ status: 'success', title: 'ZIP listo', description: 'La descarga está disponible.', duration: 3000, isClosable: true });
    };
    const onZipFailed = (e) => {
      const { error, informeId } = e.detail || {};
      pushNotification({
        id: `zip-failed-${informeId || 'na'}-${Date.now()}`, type: 'zip-error',
        message: `ZIP falló${informeId ? ` (Informe #${informeId})` : ''}${error ? `: ${error}` : ''}`, read: false,
      });
      toast({ status: 'error', title: 'ZIP falló', description: error || 'Desconocido', duration: 3000, isClosable: true });
    };
    const onNotify = (e) => {
      const d = e.detail || {};
      pushNotification({ id: d.id || `notify-${Date.now()}`, type: 'notify', message: d.message || 'Notificación', link: d.link || null, read: false });
      toast({ status: 'info', title: 'Aviso', description: d.message || 'Notificación', duration: 3000, isClosable: true });
    };
    window.addEventListener('zip-finished', onZipFinished);
    window.addEventListener('zip-failed',   onZipFailed);
    window.addEventListener('notify',       onNotify);
    return () => {
      window.removeEventListener('zip-finished', onZipFinished);
      window.removeEventListener('zip-failed',   onZipFailed);
      window.removeEventListener('notify',       onNotify);
    };
  }, [toast]);

  /* SSE (opción B) */
  useAdminNotifyStream(authToken);

  useEffect(() => {
    if (!useHeaderV2Pro) return;
    let interval = null;

    const startInterval = () => {
      if (interval) return;
      interval = setInterval(refreshProNotifications, 60000);
    };
    const stopInterval = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    // Fix jobs-polling-y-toast-sse (aprobado): pausar por completo
    // (cancelar el interval, no solo saltear el tick) mientras la
    // pestaña no está visible -- al volver, un fetch inmediato y se
    // reanuda el ciclo. Mismo patrón que el de jobs, debajo.
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        refreshProNotifications();
        startInterval();
      }
    };

    if (!document.hidden) {
      refreshProNotifications();
      startInterval();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [useHeaderV2Pro, refreshProNotifications]);

  useEffect(() => {
    if (!useHeaderV2Pro) return;
    let alive = true;
    let interval = null;
    const JOBS_POLL_INTERVAL_MS = 10000;

    const pollJobs = async () => {
      try {
        const res = await ApiConsume('META__JOBS_ACTIVE');
        if (!alive) return;
        if (res.status >= 200 && res.status < 400) {
          setAsyncJobs(res.data?.results || []);
        }
      } catch (error) {
        if (alive) console.error('Jobs polling failed', error);
      }
    };

    const startInterval = () => {
      if (interval) return;
      interval = setInterval(pollJobs, JOBS_POLL_INTERVAL_MS);
    };
    const stopInterval = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    // Fix jobs-polling-y-toast-sse (aprobado): Paso 0 confirmó que este
    // setInterval de 5s, corriendo sin pausa en TODAS las pantallas para
    // cada admin logueado, alcanza solo para explicar el 76% del
    // tráfico diario -- no había un bug de dependencias. Acá: 5000 ->
    // 10000ms, y pausa completa (cancela el interval, no solo saltea el
    // tick) mientras document.hidden -- al volver a visible, un fetch
    // inmediato y se reanuda el ciclo. `alive` sigue local al efecto a
    // propósito (NO isMountedRef -- roto bajo StrictMode, ver Paso 0 A6,
    // fuera de alcance de este fix).
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        pollJobs();
        startInterval();
      }
    };

    if (!document.hidden) {
      pollJobs();
      startInterval();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      alive = false;
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [useHeaderV2Pro]);

  useEffect(() => {
    if (!useHeaderV2Pro) return;
    let alive = true;
    const fetchWeather = async () => {
      try {
        const res = await ApiConsume('META__WEATHER');
        if (!alive) return;
        if (res.status >= 200 && res.status < 400) {
          setWeatherMeta(res.data || null);
        }
      } catch (error) {
        if (alive) console.error('Weather fetch failed', error);
      }
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [useHeaderV2Pro]);

  useEffect(() => {
    if (!useHeaderV2Pro) return;
    if (!selectedConsorcioId) {
      setQuickBalance(null);
      return;
    }
    let alive = true;
    const fetchBalance = async () => {
      try {
        const res = await ApiConsume('CONSORTIUMS__QUICK_BALANCE', 'GET', {}, selectedConsorcioId);
        if (!alive) return;
        if (res.status >= 200 && res.status < 400) {
          setQuickBalance(res.data || null);
        }
      } catch (error) {
        if (alive) console.error('Quick balance fetch failed', error);
      }
    };
    fetchBalance();
    const interval = setInterval(fetchBalance, 5 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [selectedConsorcioId, useHeaderV2Pro]);

  /* BOT events → contadores + notificaciones (links a rutas pedidas) */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const onCob = (e) => {
      const d = e.detail || {};
      setCounts(s => ({ ...s, cobranzas: s.cobranzas + 1 }));
      pushNotification({
        id: `bot-cobranza-${d.id ?? Date.now()}`,
        type: 'bot-cobranza',
        message: `Cobranza BOT ${d.monto ? `\$${Number(d.monto).toLocaleString('es-AR',{ minimumFractionDigits:2 })}` : ''}${d.uf ? ` — UF ${d.uf}` : ''}${d.propietario ? ` — ${d.propietario}` : ''}`,
        link: d.link || ROUTES.COBRANZAS,
        read: false,
        icon: <FaMoneyBillWave color="#16a34a" />,
      });
    };
    const onRec = (e) => {
      const d = e.detail || {};
      setCounts(s => ({ ...s, reclamos: s.reclamos + 1 }));
      pushNotification({
        id: `bot-reclamo-${d.id ?? Date.now()}`,
        type: 'bot-reclamo',
        message: `Reclamo #${d.numero || d.id || ''}${d.resumen ? ` — ${d.resumen}` : ''}`,
        link: d.link || ROUTES.RECLAMOS,
        read: false,
        icon: <FaExclamationTriangle color="#ecc94b" />,
      });
    };
    const onRes = (e) => {
      const d = e.detail || {};
      setCounts(s => ({ ...s, reservas: s.reservas + 1 }));
      pushNotification({
        id: `bot-reserva-${d.id ?? Date.now()}`,
        type: 'bot-reserva',
        message: `Reserva ${d.amenity ? `'${d.amenity}'` : ''}${d.fecha ? ` — ${d.fecha}` : ''}`,
        link: d.link || ROUTES.RESERVAS,
        read: false,
        icon: <FaCalendarCheck color="#3182ce" />,
      });
    };
    window.addEventListener('bot-cobranza', onCob);
    window.addEventListener('bot-reclamo',  onRec);
    window.addEventListener('bot-reserva',  onRes);
    return () => {
      window.removeEventListener('bot-cobranza', onCob);
      window.removeEventListener('bot-reclamo',  onRec);
      window.removeEventListener('bot-reserva',  onRes);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handler = (event) => {
      const detail = event.detail || {};
      const currentPathname = latestLocationRef.current?.pathname || location.pathname;
      const currentSearch = latestLocationRef.current?.search || location.search;
      const currentUiContext = latestUiContextRef.current || uiContext;
      traceWamNav('EVENT_ADDONS_FORBIDDEN_RECEIVED', {
        detail,
        pathname: currentPathname,
        search: currentSearch,
        uiContext: currentUiContext,
        storedContext:
          typeof window !== 'undefined'
            ? window.localStorage.getItem(CONTEXT_STORAGE_KEY)
            : null,
      });
      if (detail?.code !== 'ADDON_REQUIRED' || detail?.addon_code !== 'CONTAB_ADMIN') {
        return;
      }
      if (typeof refreshAddonStatus === 'function') {
        traceWamNav('REFRESH_ADDON_STATUS', {
          reason: 'addon-forbidden',
          pathname: currentPathname,
          search: currentSearch,
          uiContext: currentUiContext,
        });
        refreshAddonStatus({ force: true, reason: 'addon-forbidden' });
      }
      const now = Date.now();
      const sessionKey = 'contab-addon-toast-at';
      const lastSession = typeof window !== 'undefined'
        ? Number(window.sessionStorage.getItem(sessionKey) || 0)
        : 0;
      const shouldToast = now - addonWarningRef.current > 4000 && now - lastSession > 4000;
      if (shouldToast) {
        toast({
          title: 'Módulo Contabilidad no habilitado',
          description: detail?.detail || 'Activá el módulo para acceder.',
          status: 'warning',
          duration: 4000,
        });
        addonWarningRef.current = now;
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(sessionKey, String(now));
        }
      }
      const explicitConsorciosTransition =
        contextTransitionTargetRef.current === UI_CONTEXTS.CONSORCIOS;
      const shouldStayInContabFlow =
        !explicitConsorciosTransition &&
        currentUiContext === UI_CONTEXTS.MI_ADMIN &&
        !isConsorciosRoutePath(currentPathname);
      traceWamNav('ADDON_FORBIDDEN_FLOW_DECISION', {
        pathname: currentPathname,
        search: currentSearch,
        uiContext: currentUiContext,
        transitionTarget: contextTransitionTargetRef.current,
        currentRouteIsConsorcios: isConsorciosRoutePath(currentPathname),
        shouldStayInContabFlow,
      });
      if (shouldStayInContabFlow && !currentPathname.startsWith('/contabilidad-admin')) {
        traceWamNav('ADDON_FORBIDDEN_NAVIGATE_TO_MI_ADMIN', {
          pathname: currentPathname,
          search: currentSearch,
          uiContext: currentUiContext,
          storedContext:
            typeof window !== 'undefined'
              ? window.localStorage.getItem(CONTEXT_STORAGE_KEY)
              : null,
          destination: '/contabilidad-admin?tab=dashboard',
          detail,
        });
        markNavPending();
        navigate('/contabilidad-admin?tab=dashboard');
      }
    };
    window.addEventListener('addons:forbidden', handler);
    return () => window.removeEventListener('addons:forbidden', handler);
  }, [markNavPending, navigate, refreshAddonStatus, toast]);

  const lastGatedPathRef = useRef(null);
  useEffect(() => {
    if (typeof refreshAddonStatus !== 'function') return;
    const matched = GATED_ROUTE_PREFIXES.find((prefix) => location.pathname.startsWith(prefix));
    if (!matched) {
      lastGatedPathRef.current = null;
      return;
    }
    const key = `${matched}::${location.pathname}`;
    if (lastGatedPathRef.current === key) return;
    lastGatedPathRef.current = key;
    traceWamNav('REFRESH_ADDON_STATUS', {
      reason: 'gated-route',
      pathname: location.pathname,
      search: location.search,
      uiContext,
      matched,
    });
    refreshAddonStatus({ force: true, reason: 'gated-route' });
  }, [GATED_ROUTE_PREFIXES, location.pathname, location.search, refreshAddonStatus, uiContext]);

  const prevUiContextRef = useRef(uiContext);
  useEffect(() => {
    const prev = prevUiContextRef.current;
    prevUiContextRef.current = uiContext;
    if (uiContext === UI_CONTEXTS.MI_ADMIN && prev !== UI_CONTEXTS.MI_ADMIN && typeof refreshAddonStatus === 'function') {
      traceWamNav('REFRESH_ADDON_STATUS', {
        reason: 'ui-context',
        pathname: location.pathname,
        search: location.search,
        uiContext,
        previousUiContext: prev,
      });
      refreshAddonStatus({ force: true, reason: 'ui-context' });
    }
  }, [location.pathname, location.search, refreshAddonStatus, uiContext]);

  const handleContextChange = useCallback(
    (next) => {
      if (!next) return;
      traceWamNav('HANDLE_CONTEXT_CHANGE', {
        previous: uiContext,
        next,
        pathname: location.pathname,
        search: location.search,
        storedContext:
          typeof window !== 'undefined'
            ? window.localStorage.getItem(CONTEXT_STORAGE_KEY)
            : null,
      });
      markNavPending();
      contextTransitionTargetRef.current = next;
      primeUiContextTransition(next);
      updateUiContext(next);
      if (next === UI_CONTEXTS.MI_ADMIN) {
        traceWamNav('HANDLE_CONTEXT_CHANGE_NAVIGATE', {
          destination: '/contabilidad-admin?tab=dashboard',
          pathname: location.pathname,
          search: location.search,
          previous: uiContext,
          next,
        });
        navigate('/contabilidad-admin?tab=dashboard');
      } else if (next === UI_CONTEXTS.CONSORCIOS && isContabRoute) {
        const consorciosDestination = lastConsorciosRouteRef.current || '/';
        traceWamNav('HANDLE_CONTEXT_CHANGE_NAVIGATE', {
          destination: consorciosDestination,
          pathname: location.pathname,
          search: location.search,
          previous: uiContext,
          next,
        });
        navigate(consorciosDestination);
      }
    },
    [isContabRoute, location.pathname, location.search, markNavPending, navigate, primeUiContextTransition, refreshAddonStatus, uiContext, updateUiContext]
  );
  const contextSwitcherProps = useMemo(
    () => ({
      uiContext,
      onChange: handleContextChange,
    }),
    [handleContextChange, uiContext]
  );

  const navGroups = useMemo(() => {
    const map = SIDEBAR_GROUP_META.reduce((acc, meta) => {
      acc[meta.key] = { ...meta, items: [] };
      return acc;
    }, {});
    NAV_ITEMS.forEach((item) => {
      const groupKey =
        item.group || (item.section === UI_CONTEXTS.MI_ADMIN ? 'mi_admin' : 'principal');
      if (!map[groupKey]) return;
      map[groupKey].items.push(item);
    });
    return SIDEBAR_GROUP_META.map((meta) => map[meta.key]);
  }, []);

  const isNavItemAllowedByCapabilities = useCallback(
    (item) => {
      const accessContext = {
        capabilities,
        isSuperuser: isSuper,
      };
      if (item?.capabilityKey) {
        return canAccessSidebarItem(item.capabilityKey, accessContext);
      }
      if (item?.link) {
        return canAccessRoutePath(item.link, accessContext);
      }
      if (Array.isArray(item?.children) && item.children.length > 0) {
        return item.children.some((child) => {
          if (!child?.link) return !isCapabilitiesStrictDefaultDeny();
          return canAccessRoutePath(child.link, accessContext);
        });
      }
      return !isCapabilitiesStrictDefaultDeny();
    },
    [capabilities, isSuper]
  );

  const routeAccessAllowed = useMemo(
    () =>
      canAccessRoutePath(location.pathname, {
        capabilities,
        isSuperuser: isSuper,
      }),
    [capabilities, isSuper, location.pathname]
  );
  const filteredQuickActions = useMemo(
    () =>
      QUICK_ACTIONS.filter((action) =>
        canAccessRoutePath(action.path, {
          capabilities,
          isSuperuser: isSuper,
        })
      ),
    [capabilities, isSuper]
  );

  const navItemsByKey = useMemo(
    () => new Map(NAV_ITEMS.map((item) => [item.key, item])),
    []
  );

  const [asistenteVisible, setAsistenteVisible] = useState(false);
  const alCambiarEstadoAsistente = useCallback((estado) => setAsistenteVisible(Boolean(estado?.habilitado)), []);

  const visibleSidebarItems = useMemo(() => {
    const orderedKeys =
      uiContext === UI_CONTEXTS.MI_ADMIN ? MI_ADMIN_PRIMARY_NAV_KEYS : CONSORCIOS_PRIMARY_NAV_KEYS;

    return orderedKeys
      .map((key) => navItemsByKey.get(key))
      .filter(Boolean)
      .filter((item) => !item.onlySuperUser || isSuper)
      .filter((item) => !item.onlyAsistente || asistenteVisible)
      .filter(isNavItemAllowedByCapabilities);
  }, [isNavItemAllowedByCapabilities, isSuper, navItemsByKey, uiContext, asistenteVisible]);

  const flatNavItems = useMemo(() => {
    const items = [];
    const appendItems = (entries, parentTitle = null, section = UI_CONTEXTS.CONSORCIOS) => {
      entries.forEach((entry) => {
        const normalizedLink = normalizeNavLink(entry.link);
        const itemTab = extractTabFromLink(entry.link);
        if (normalizedLink) {
          items.push({
            title: entry.navTitle || entry.title,
            link: normalizedLink,
            originalLink: entry.link,
            parent: parentTitle,
            section,
            tab: itemTab ? normalizeContabTab(itemTab) : null,
            contabTabs: Array.isArray(entry.contabTabs) ? entry.contabTabs : null,
            activePrefix: entry.activePrefix,
            matchSubroutes: entry.matchSubroutes,
            isContabItem: normalizedLink === '/contabilidad-admin',
          });
        }
        if (Array.isArray(entry.children) && entry.children.length > 0) {
          appendItems(entry.children, entry.navTitle || entry.title, section);
        }
      });
    };
    navGroups.forEach((group) => appendItems(group.items || [], null, group.section));
    return items;
  }, [navGroups]);

  const navInfo = useMemo(() => {
    const path = location.pathname || '/';
    let match = { title: 'Panel', parent: null, linkLength: 0 };
    flatNavItems.forEach((item) => {
      if (!item.link) return;
      const activeTab = contabActiveTab || 'dashboard';
      const matchesCurrentRoute = item.isContabItem
        ? isContabRoute &&
          (
            item.contabTabs?.includes(activeTab) ||
            (item.tab ? item.tab === activeTab : activeTab === 'dashboard')
          )
        : doesPathMatchNavPath(path, item.originalLink || item.link, item.activePrefix, item.matchSubroutes);
      if (matchesCurrentRoute) {
        const matchWeight = item.link.length + (item.tab || item.contabTabs ? 10 : 0);
        if (matchWeight >= match.linkLength) {
          match = { title: item.title, parent: item.parent, linkLength: matchWeight };
        }
      }
    });
    return match;
  }, [contabActiveTab, flatNavItems, isContabRoute, location.pathname]);

  const periodStatus = user?.current_period_status || null;
  const currentJobStatus = null;
  const proUnreadCount = useMemo(
    () => proNotifications.filter((item) => (item.state || 'unread') !== 'read').length,
    [proNotifications]
  );

  /* ---------- Header full-width (fixed) ---------- */
  const LegacyHeader = (
    <Flex
      as="header"
      position="fixed"
      top={0}
      left={0}
      right={0}
      h={`${headerHeightValue}px`}
      zIndex={200}
      align="center"
      justify="space-between"
      px={{ base: 2, md: 4, xl: 6 }}
      pl={{
        base: 2,
        md: `${Math.max(collapsedSidebarWidthValue, 0) + 24}px`,
      }}
      bg={headerBg}
      color="white"
      backdropFilter="blur(6px)"
      boxShadow="sm"
    >
      {/* Mobile burger */}
      <IconButton aria-label="Abrir menú" icon={<FaBars />} display={{ base:'inline-flex', md:'none' }}
                  onClick={sidebarDrawer.onOpen} variant="ghost" colorScheme="whiteAlpha" />
      <Box ml={2} display={{ base: 'inline-flex', md: 'none' }}>
        <ContextSwitcherControl {...contextSwitcherProps} size="sm" />
      </Box>

      {/* Left pills */}
      <HStack spacing={2}>
        <IconPill
          label="Cobranzas BOT"
          icon={FaMoneyBillWave}
          tint="green"
          badge={counts.cobranzas}
          onClick={() => {
            setCounts(s => ({ ...s, cobranzas: 0 }));
            navigate(ROUTES.COBRANZAS);
          }}
        />
        <IconPill
          label="Reclamos"
          icon={FaExclamationTriangle}
          tint="yellow"
          badge={counts.reclamos}
          onClick={() => {
            setCounts(s => ({ ...s, reclamos: 0 }));
            navigate(ROUTES.RECLAMOS);
          }}
        />
        <IconPill
          label="Reservas"
          icon={FaCalendarCheck}
          tint="blue"
          badge={counts.reservas}
          onClick={() => {
            setCounts(s => ({ ...s, reservas: 0 }));
            navigate(ROUTES.RESERVAS);
          }}
        />
      </HStack>
      <Box ml={4} display={{ base: 'none', md: 'block' }}>
        <ContextSwitcherControl {...contextSwitcherProps} size="sm" />
      </Box>

      {/* Center: time + weather (desktop) */}
      <HStack spacing={6} flex={1} justify="center" display={{ base:'none', md:'flex' }}>
        <HStack spacing={3}><FaClock /><Text fontSize="md">{new Date(now).toLocaleTimeString('es-AR',{hour12:false})}</Text></HStack>
        <HStack spacing={3}><FaCloudSun /><WeatherComponent /></HStack>
      </HStack>

      {/* Right: theme + online + bell + user */}
      <HStack spacing={2}>
        <IconPill label={colorMode==='light'?'Modo oscuro':'Modo claro'} icon={colorMode==='light'?FaMoon:FaSun}
                  tint="purple" onClick={toggleColorMode}/>
        <Badge colorScheme={online ? 'green' : 'red'}>{online ? 'Online' : 'Offline'}</Badge>
        <Tooltip hasArrow label={unread ? `${unread} notificaciones` : 'Sin notificaciones'} placement="bottom">
          <IconButton aria-label="Notificaciones" onClick={dock.onOpen} variant="ghost" colorScheme="whiteAlpha"
                      icon={<FaBell color={unread ? '#ff6b6b' : 'currentColor'} />}/>
        </Tooltip>
        <Menu>
          <MenuButton as={Button} variant="ghost" colorScheme="whiteAlpha" rightIcon={<FaUserCircle />} px={2} py={1}>
            <HStack spacing={2}>
              <Avatar size="sm" bg="gray.400" color="white" name={`${user?.first_name || ''} ${user?.last_name || ''}`}/>
              <Text display={{ base:'none', md:'inline' }}>
                {user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username || 'Usuario'}
              </Text>
            </HStack>
          </MenuButton>
          <MenuList>
            <MenuItem icon={<FaUserCircle />}>Perfil</MenuItem>
            <MenuDivider />
            <MenuItem icon={<FaSignOutAlt />} color="red.400"
              onClick={handleLogout}>
              Cerrar sesión
            </MenuItem>
          </MenuList>
        </Menu>
      </HStack>
    </Flex>
  );

  const LegacySidebarHover = (
    <Box
      as="nav"
      data-testid="wam-sidebar"
      bg={useColorModeValue('linear-gradient(180deg, #0f172a 0%, #172554 100%)', 'linear-gradient(180deg, #020617 0%, #0f172a 100%)')}
      color={useColorModeValue('whiteAlpha.900','whiteAlpha.900')}
      w={`${sidebarWidthLegacy}px`}
      position="fixed"
      top={`${headerHeightValue}px`}
      left={0}
      h={`calc(100dvh - ${headerHeightValue}px)`}
      borderRightWidth="1px"
      borderColor={useColorModeValue('whiteAlpha.200', 'whiteAlpha.100')}
      boxShadow="0 24px 50px rgba(2, 6, 23, 0.35)"
      zIndex={180}
      transition="width .18s ease"
      display={{ base:'none', md:'block' }}
      onMouseEnter={() => setLegacySidebarCollapsed(false)}
      onMouseLeave={() => setLegacySidebarCollapsed(true)}
    >
      <Flex direction="column" h="100%">
        <Box px={legacySidebarCollapsed ? 2 : 4} py={3}>
          <VStack align="flex-start" spacing={legacySidebarCollapsed ? 0 : 1}>
            <Text fontWeight="bold" fontSize="lg" color="white">
              WAM
            </Text>
            {!legacySidebarCollapsed ? (
              <Text fontSize="xs" color="whiteAlpha.700" textTransform="uppercase" letterSpacing="0.14em">
                Control operativo
              </Text>
            ) : null}
          </VStack>
        </Box>
        <Box
          flex="1"
          minH={0}
          overflowY="auto"
          px={legacySidebarCollapsed ? 1 : 2}
          pb={3}
          sx={{
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '8px' },
          }}
        >
          <VStack align="stretch" spacing={1}>
            {visibleSidebarItems.map((item) => {
              const visibleChildren = Array.isArray(item.children)
                ? item.children.filter(isNavItemAllowedByCapabilities)
                : [];
              const hasChildren = visibleChildren.length > 0;
              const itemToRender = hasChildren ? { ...item, children: visibleChildren } : item;
              const active = isNavItemActive({
                item,
                pathname: location.pathname,
                isContabRoute,
                contabActiveTab,
              });
              const anyChild =
                hasChildren &&
                visibleChildren.some((child) =>
                  isNavItemActive({
                    item: child,
                    pathname: location.pathname,
                    isContabRoute,
                    contabActiveTab,
                  })
                );
              const rowBg = active || anyChild ? 'blue.600' : 'transparent';
              const rowCol = active || anyChild ? 'white' : 'inherit';
              const lockMeta = getAddonLockMeta(item.addonCode);
              const locked = lockMeta.locked;
              const childTabActive =
                isContabRoute && isTaxTab && item.key === 'contab_impuestos';
              const forceOpen =
                (isContabRoute && item.key === 'contab_admin') || childTabActive;
              const storedOpen =
                item.key && Object.prototype.hasOwnProperty.call(openGroups, item.key)
                  ? openGroups[item.key]
                  : false;
              const isGroupOpen = forceOpen || Boolean(storedOpen) || Boolean(anyChild);
              const handleGroupToggle = (next) => {
                if (!item.key) return;
                setOpenGroups((prev) => ({
                  ...prev,
                  [item.key]:
                    typeof next === 'boolean'
                      ? next
                      : !prev[item.key],
                }));
              };

              if (!hasChildren) {
                return (
                  <Tooltip
                    key={item.link || item.title}
                    label={item.title}
                    placement="right"
                    isDisabled={!legacySidebarCollapsed}
                  >
                    <Box
                      as={Link}
                      to={item.link || '#'}
                      data-testid={`nav-item-${item.key}`}
                      data-active={active ? 'true' : 'false'}
                      display="flex"
                      alignItems="center"
                      p={legacySidebarCollapsed ? 2 : 3}
                      borderRadius="md"
                      bg={rowBg}
                      color={rowCol}
                      _hover={{ bg: 'rgba(37, 99, 235, 0.88)', color: 'white' }}
                      transition="all .14s"
                      onClick={(e) => {
                        if (locked) {
                          e.preventDefault();
                          showAddonLockToast(lockMeta);
                          return;
                        }
                        if (item.link) {
                          markNavPending();
                        }
                        handleNavClick(item.section);
                      }}
                    >
                      <Box fontSize="xl" minW="30px" position="relative">
                        {item.icon}
                        {locked && legacySidebarCollapsed && (
                          <Box
                            as={FaLock}
                            position="absolute"
                            top="-6px"
                            right="-8px"
                            fontSize="0.65rem"
                            color="orange.300"
                          />
                        )}
                      </Box>
                      {!legacySidebarCollapsed && (
                        <HStack ml={2} spacing={2}>
                          <Text>{item.title}</Text>
                          {locked && <Box as={FaLock} fontSize="0.8rem" color="orange.300" />}
                        </HStack>
                      )}
                    </Box>
                  </Tooltip>
                );
              }

              return (
                <SidebarGroupHover
                  key={item.title}
                  item={itemToRender}
                  sectionKey={item.section}
                  openDefault={anyChild}
                  forceOpen={forceOpen}
                  isOpen={isGroupOpen}
                  onToggle={handleGroupToggle}
                  collapsed={legacySidebarCollapsed}
                  onNavigate={handleNavClick}
                  addonStatusMap={addonStatusMap}
                  addonsLoading={addonsLoading}
                  currentPath={location.pathname}
                  isContabRoute={isContabRoute}
                  contabActiveTab={contabActiveTab}
                  markNavPending={markNavPending}
                  locked={locked}
                  lockMeta={lockMeta}
                  onLockedAttempt={() => showAddonLockToast(lockMeta)}
                  getAddonLockMeta={getAddonLockMeta}
                />
              );
            })}
          </VStack>
        </Box>
        <Box
          px={legacySidebarCollapsed ? 1 : 2}
          py={3}
          borderTopWidth="1px"
          borderColor="whiteAlpha.200"
        >
          <Tooltip
            label="Cerrar sesión"
            placement="right"
            isDisabled={!legacySidebarCollapsed}
          >
            <Box
              as="button"
              type="button"
              onClick={handleLogout}
              data-testid="sidebar-logout-button"
              width="100%"
              display="flex"
              alignItems="center"
              p={legacySidebarCollapsed ? 2 : 3}
              borderRadius="md"
              _hover={{ bg: 'rgba(248, 113, 113, 0.18)', color: 'white' }}
              transition="all .14s"
            >
              <Box fontSize="xl" minW="30px">
                <FaSignOutAlt />
              </Box>
              {!legacySidebarCollapsed && (
                <Text ml={2} color="inherit">
                  Cerrar sesión
                </Text>
              )}
            </Box>
          </Tooltip>
        </Box>
      </Flex>
    </Box>
  );

  /* Sidebar Drawer (mobile) */
  const LegacySidebarMobile = (
    <Drawer isOpen={sidebarDrawer.isOpen} placement="left" onClose={sidebarDrawer.onClose} size="xs">
      <DrawerOverlay />
      <DrawerContent
        data-testid="wam-sidebar-drawer"
        h="100dvh"
        maxH="100dvh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        bg={useColorModeValue('linear-gradient(180deg, #0f172a 0%, #172554 100%)', 'linear-gradient(180deg, #020617 0%, #0f172a 100%)')}
        color="whiteAlpha.900"
      >
        <DrawerHeader borderBottomWidth="1px" borderColor="whiteAlpha.200">
          Menú WAM
        </DrawerHeader>
        <DrawerBody
          p={0}
          overflowY="auto"
          sx={{ WebkitOverflowScrolling: 'touch' }}
          overscrollBehaviorY="contain"
        >
          <VStack align="stretch" spacing={3} p={3}>
            <ContextSwitcherControl {...contextSwitcherProps} fullWidth />
            {visibleSidebarItems.map((item) => {
              const visibleChildren = Array.isArray(item.children)
                ? item.children.filter(isNavItemAllowedByCapabilities)
                : [];
              const hasChildren = visibleChildren.length > 0;
              const lockMeta = getAddonLockMeta(item.addonCode);
              const locked = lockMeta.locked;
              const active = isNavItemActive({
                item,
                pathname: location.pathname,
                isContabRoute,
                contabActiveTab,
              });

              if (!hasChildren) {
                return (
                  <Box
                    as={Link}
                    to={item.link || '#'}
                    key={item.title}
                    data-testid={`nav-item-${item.key}-mobile`}
                    data-active={active ? 'true' : 'false'}
                    onClick={(e) => {
                      if (locked) {
                        e.preventDefault();
                        showAddonLockToast(lockMeta);
                        return;
                      }
                      if (item.link) {
                        markNavPending();
                      }
                      handleNavClick(item.section, true);
                    }}
                    p={2}
                    borderRadius="md"
                    bg={active ? 'rgba(37, 99, 235, 0.22)' : 'transparent'}
                    _hover={{ bg: 'rgba(255,255,255,0.08)' }}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <HStack spacing={3}>
                      <Box fontSize="lg">{item.icon}</Box>
                      <Text>{item.title}</Text>
                    </HStack>
                    {locked && <FaLock color="#dd6b20" />}
                  </Box>
                );
              }

              const itemStateKey = `mobile:${item.key || item.title}`;
              const anyChildActive = visibleChildren.some((child) =>
                isNavItemActive({
                  item: child,
                  pathname: location.pathname,
                  isContabRoute,
                  contabActiveTab,
                })
              );
              const storedItemOpen = Object.prototype.hasOwnProperty.call(openGroups, itemStateKey)
                ? openGroups[itemStateKey]
                : undefined;
              const isItemOpen =
                typeof storedItemOpen === 'boolean' ? storedItemOpen : anyChildActive;

              return (
                <Box key={item.title} borderWidth="1px" borderRadius="md" borderColor="whiteAlpha.200" p={2}>
                  <Box
                    as="button"
                    type="button"
                    width="100%"
                    textAlign="left"
                    onClick={() =>
                      setOpenGroups((prev) => ({
                        ...prev,
                        [itemStateKey]: !isItemOpen,
                      }))
                    }
                  >
                    <HStack justify="space-between">
                      <Text fontWeight="bold" fontSize="sm">
                        {item.title}
                      </Text>
                      {isItemOpen ? <FaChevronDown /> : <FaChevronRight />}
                    </HStack>
                  </Box>
                  {isItemOpen && (
                    <VStack align="stretch" pl={3} spacing={1} mt={2}>
                      {visibleChildren.map((child) => {
                        const childActive = isNavItemActive({
                          item: child,
                          pathname: location.pathname,
                          isContabRoute,
                          contabActiveTab,
                        });
                        const childLockMeta = getAddonLockMeta(child.addonCode || item.addonCode);
                        const childLocked = childLockMeta.locked || locked;
                        const lockMetaForChild = childLockMeta.locked ? childLockMeta : lockMeta;
                        return (
                          <Box
                            as={Link}
                            to={child.link}
                            key={child.title}
                            onClick={(e) => {
                              if (childLocked) {
                                e.preventDefault();
                                showAddonLockToast(lockMetaForChild);
                                return;
                              }
                              markNavPending();
                              handleNavClick(item.section, true);
                            }}
                            p={1}
                            borderRadius="md"
                            bg={childActive ? 'rgba(37, 99, 235, 0.22)' : 'transparent'}
                            _hover={{ bg: 'rgba(255,255,255,0.08)' }}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Text>{child.title}</Text>
                            {childLocked && <FaLock color="#dd6b20" />}
                          </Box>
                        );
                      })}
                    </VStack>
                  )}
                </Box>
              );
            })}
            <Divider borderColor="whiteAlpha.200" />
            <Button
              leftIcon={<FaSignOutAlt />}
              variant="outline"
              colorScheme="red"
              justifyContent="flex-start"
              onClick={handleLogout}
              data-testid="sidebar-logout-button-mobile"
            >
              Cerrar sesión
            </Button>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  /* ---------- Layout principal ---------- */
  const globalStyles = useMemo(
    () => ({
      body: {
        backgroundColor: bodyBgColor,
        overflowX: 'hidden',
      },
      '.responsive-table-container': {
        overflowX: 'auto',
        borderRadius: '1rem',
        borderWidth: '1px',
        borderColor: responsiveBorderColor,
        backgroundColor: responsiveBackground,
        boxShadow: responsiveShadow,
        marginTop: '1.5rem',
        marginBottom: '1.5rem',
      },
      '.responsive-table-container table': {
        width: '100%',
        minWidth: '760px',
      },
      '@media (max-width: 768px)': {
        '.responsive-table-container table': {
          minWidth: '640px',
        },
      },
      '.responsive-table-container::-webkit-scrollbar': {
        height: '8px',
      },
      '.responsive-table-container::-webkit-scrollbar-thumb': {
        borderRadius: '999px',
        background: scrollbarThumb,
      },
      '[data-compact-tables="1"] .responsive-table-container table th, [data-compact-tables="1"] .responsive-table-container table td':
        {
          paddingTop: '0.35rem',
          paddingBottom: '0.35rem',
          fontSize: '0.85rem',
        },
      '[data-compact-tables="1"] .chakra-table th, [data-compact-tables="1"] .chakra-table td': {
        paddingTop: '0.35rem',
        paddingBottom: '0.35rem',
        fontSize: '0.85rem',
      },
      /* Auto-scroll via :has() — pure CSS, zero DOM manipulation */
      '.private-content :has(> table.chakra-table):not(.responsive-table-container):not(.chakra-table__container)': {
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      },
      '.private-content :has(> table.chakra-table):not(.responsive-table-container):not(.chakra-table__container)::-webkit-scrollbar': {
        height: '6px',
      },
      '.private-content :has(> table.chakra-table):not(.responsive-table-container):not(.chakra-table__container)::-webkit-scrollbar-thumb': {
        borderRadius: '999px',
        background: scrollbarThumb,
      },
      /*
       * Sticky last column — inert on non-scrolling tables, activates only when parent scrolls.
       * Selector envuelto en :where() para que su especificidad sea 0: así, cualquier tabla que
       * ya defina su propio bg/zIndex/position en la última columna (ej. CommunicationDispatchControlPage,
       * que ya usa position="sticky" right={0} con su propio bg) gana automáticamente por sobre esta
       * regla de conveniencia, en vez de que esta (antes con especificidad más alta que una clase
       * generada por Chakra) la pisara sin que nadie lo notara.
       * `background` sólido (no 'inherit'): con 'inherit' la celda tomaba el `background` del <tr>,
       * que casi nunca está seteado explícitamente (=> transparente), dejando pasar visualmente el
       * contenido de las columnas que se desplazan por detrás al hacer scroll horizontal.
       */
      ':where(table.chakra-table) th:last-child, :where(table.chakra-table) td:last-child': {
        position: 'sticky',
        right: 0,
        zIndex: 2,
        background: stickyColumnBg,
        boxShadow: '-4px 0 8px -2px rgba(0,0,0,0.08)',
      },
      ':where(table.chakra-table) th:last-child': {
        zIndex: 3,
      },
      '@media (max-width: 48em)': {
        '.chakra-modal__content': {
          maxWidth: 'calc(100vw - 1.5rem) !important',
          maxHeight: 'calc(100dvh - 4rem) !important',
          overflowY: 'auto !important',
        },
        '.chakra-modal__body': {
          overflowY: 'auto !important',
        },
      },
    }),
    [bodyBgColor, responsiveBackground, responsiveBorderColor, responsiveShadow, scrollbarThumb, stickyColumnBg]
  );

  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      return undefined;
    }
    const isMarked = (node) =>
      node?.dataset?.responsiveWrapped === '1' || node?.dataset?.responsiveWrapped === 'true';

    const ensureContainerStyling = (element) => {
      if (!element) return;
      const attrValue = element.getAttribute('data-responsive-table-wrapper');
      if (attrValue && attrValue !== '1') {
        element.setAttribute('data-responsive-table-wrapper', '1');
      }
      element.classList.add('responsive-table-container');
      element.dataset.responsiveTableWrapper = '1';
      const tables = element.matches('table') ? [element] : element.querySelectorAll('table');
      tables.forEach((tbl) => {
        tbl.dataset.responsiveWrapped = '1';
      });
    };

    const wrapStandaloneTable = (table) => {
      if (!table || isMarked(table)) return;
      const parent = table.parentElement;
      if (!parent) return;
      if (
        parent.classList.contains('responsive-table-container') ||
        parent.matches('[data-responsive-table-wrapper]')
      ) {
        table.dataset.responsiveWrapped = '1';
        return;
      }
      if (parent.dataset.preventResponsiveWrap === 'true') {
        table.dataset.responsiveWrapped = '1';
        return;
      }
      const wrapper = document.createElement('div');
      wrapper.className = 'responsive-table-container';
      wrapper.dataset.responsiveTableWrapper = '1';
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
      table.dataset.responsiveWrapped = '1';
    };

    const wrapTables = () => {
      const optInContainers = document.querySelectorAll('[data-responsive-table-wrapper]');
      optInContainers.forEach((container) => ensureContainerStyling(container));

      const optInTables = document.querySelectorAll('table.wam-responsive-table');
      optInTables.forEach((table) => wrapStandaloneTable(table));
    };
    wrapTables();
    const observer = new MutationObserver(() => wrapTables());
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const headerContent = useHeaderV2 ? (
    <HeaderV2
      drawerDisclosure={sidebarDrawer}
      title={navInfo.title || 'Panel'}
      parentTitle={navInfo.parent}
      unreadCount={useHeaderV2Pro ? proUnreadCount : unread}
      onOpenNotifications={dock.onOpen}
      onNavigate={(path) => navigate(path)}
      onLogout={handleLogout}
      user={user}
      searchQuery={searchQuery}
      onSearchQueryChange={handleSearchQueryChange}
      searchResults={searchResults}
      searchLoading={searchLoading}
      searchError={searchError}
      onSearchSubmit={runSearchNow}
      onSearchSelect={handleSearchNavigate}
      onSearchClear={() => {
        setSearchQuery('');
        clearSearchState();
      }}
      headerHeight={headerHeightValue}
      periodStatus={periodStatus}
      quickActions={filteredQuickActions}
      notifications={useHeaderV2Pro ? proNotifications : notifications}
      notificationCounts={useHeaderV2Pro ? proNotificationCounts : counts}
      jobStatus={useHeaderV2Pro ? null : currentJobStatus}
      weatherMeta={weatherMeta}
      quickBalance={quickBalance}
      jobs={asyncJobs}
      onConsorcioChange={handleConsorcioChange}
      onConsorciosLoaded={setConsorciosLista}
      selectedConsorcio={selectedConsorcio}
      currentPath={location.pathname}
      onRefreshNotifications={useHeaderV2Pro ? refreshProNotifications : null}
      contextSelectorNode={<ContextSwitcherControl {...contextSwitcherProps} size="sm" />}
    />
  ) : LegacyHeader;

  const hasSlotChildren = React.Children.count(children) > 0;
  if (!routeAccessAllowed) {
    return <NotFoundPage />;
  }

  return (
    <UiPreferencesProvider>
      <AsistenteProvider
        onEstado={alCambiarEstadoAsistente}
        consorcio={selectedConsorcio}
        consorcios={consorciosLista}
        onCambiarConsorcio={handleConsorcioChange}
      >
      <UiSectionContext.Provider value={{ uiContext, setUiContext: handleContextChange }}>
        <Global styles={globalStyles} />
        {headerContent}
        {LegacySidebarHover}
        {LegacySidebarMobile}

        {/* Contenido: header full + espacio mínimo del sidebar colapsado (no salta al hover) */}
        <AsistenteMain
          as="main"
          pt={`${headerHeightValue}px`}
          pl={{ base: 0, md: `${collapsedSidebarWidthValue}px` }}
          minH="100dvh"
          bg={mainBg}
          transition="padding-left .2s ease, padding-right .2s ease"
          overflowX="hidden"
          sx={{ WebkitOverflowScrolling: 'touch' }}
        >
          <Box
            className="private-content"
            w="100%"
            maxW={effectiveContentMaxWidth}
            minW={0}
            mx="auto"
            px="clamp(12px, 2.2vw, 40px)"
            pt={{ base: 2, md: 3 }}
            pb={{ base: 3, md: 4 }}
            boxSizing="border-box"
          >
            {hasSlotChildren ? children : <Outlet />}
          </Box>
        </AsistenteMain>
        <AsistentePanel top={headerHeightValue} />

        <ActivityDock
          isOpen={dock.isOpen}
          onClose={dock.onClose}
          notifications={notifications}
          counts={counts}
          onRead={(id) => setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)))}
          onReadAll={() => setNotifications(prev => prev.map(n => ({ ...n, read: true })))}
          onClearAll={() => setNotifications([])}
          onNavigate={(link) => {
            if (!link) return;
            // Si viene una URL absoluta, abrir nueva pestaña; si es path interno, usar navigate
            if (/^https?:\/\//i.test(link)) openUrl(link);
            else navigate(link);
            dock.onClose();
          }}
        />
      </UiSectionContext.Provider>
      </AsistenteProvider>
    </UiPreferencesProvider>
  );
}

/* Grupo del sidebar con hijos (hover) */
function SidebarGroupHover({
  item,
  sectionKey,
  openDefault,
  forceOpen = false,
  collapsed,
  onNavigate,
  addonStatusMap,
  addonsLoading,
  currentPath,
  isContabRoute,
  contabActiveTab,
  markNavPending,
  isOpen,
  onToggle,
  locked = false,
  lockMeta = null,
  onLockedAttempt,
  getAddonLockMeta,
}) {
  const navigate = useNavigate();
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const open = typeof isOpen === 'boolean' ? isOpen : !!openDefault || forceOpen;
  const [subOpen, setSubOpen] = useState({});
  const isTaxTab = ['impuestos', 'iva', 'iibb', 'ganancias_pf'].includes(contabActiveTab || '');

  useEffect(() => {
    if (isTaxTab) {
      setSubOpen((prev) => (prev.contab_impuestos ? prev : { ...prev, contab_impuestos: true }));
    }
  }, [isTaxTab]);

  const handleChildNavigate = (link, lockOverride) => {
    if (!link) return;
    const effectiveLock = lockOverride?.locked ? lockOverride : lockMeta?.locked ? lockMeta : null;
    if (effectiveLock?.locked) {
      onLockedAttempt?.(effectiveLock);
      return;
    }
    markNavPending?.();
    onNavigate?.(sectionKey);
    navigate(link);
  };

  const handleGroupClick = () => {
    if (locked && lockMeta?.locked) {
      onLockedAttempt?.(lockMeta);
      return;
    }
    if (!hasChildren) {
      handleChildNavigate(item.link, lockMeta);
      return;
    }
    if (!open) {
      onToggle?.(true);
      if (item.link) {
        handleChildNavigate(item.link, lockMeta);
      }
    } else if (!forceOpen) {
      onToggle?.(false);
    }
  };

  return (
    <Box>
      <Tooltip label={item.title} placement="right" isDisabled={!collapsed}>
        <Box
          role="button"
          onClick={handleGroupClick}
          display="flex"
          alignItems="center"
          p={collapsed ? 2 : 3}
          borderRadius="md"
          _hover={{ bg:'blue.700', color:'white' }}
          transition="all .14s"
        >
          <Box fontSize="xl" minW="30px">{item.icon}</Box>
          {!collapsed && (
            <>
              <Text ml={2} flex="1">{item.title}</Text>
              {locked && <FaLock color="#dd6b20" />}
              {hasChildren && (open ? <FaChevronDown /> : <FaChevronRight />)}
            </>
          )}
        </Box>
      </Tooltip>
      {hasChildren && (
        <Collapse in={open}>
          <VStack align="stretch" spacing={1} ml={collapsed ? 0 : 6} mt={1} mb={2}>
          {item.children.map((ch) => {
            const isActive = isNavItemActive({
              item: ch,
              pathname: currentPath,
              isContabRoute,
              contabActiveTab,
            });
            const childLockMeta = getAddonLockMeta ? getAddonLockMeta(ch.addonCode || item.addonCode) : null;
            const childLocked = Boolean(childLockMeta?.locked || lockMeta?.locked);
            const lockMetaForChild = childLockMeta?.locked ? childLockMeta : lockMeta;
            const IconComp = ch.icon;
            const iconNode = IconComp
              ? React.isValidElement(IconComp)
                ? IconComp
                : <IconComp />
              : <FaChevronRight />;
            const chHasChildren = Array.isArray(ch.children) && ch.children.length > 0;
            if (chHasChildren) {
              const subKey = ch.key || ch.title;
              const forceOpenSub = subKey === 'contab_impuestos' && isTaxTab;
              const subIsOpen = forceOpenSub || !!subOpen[subKey];
              const anyGrandChildActive = ch.children.some((gc) =>
                isNavItemActive({
                  item: gc,
                  pathname: currentPath,
                  isContabRoute,
                  contabActiveTab,
                })
              );
              const onSubHeaderClick = () => {
                if (!subIsOpen) {
                  setSubOpen((prev) => ({ ...prev, [subKey]: true }));
                  if (ch.link) {
                    handleChildNavigate(ch.link, lockMetaForChild);
                  }
                  return;
                }
                if (!forceOpenSub) {
                  setSubOpen((prev) => ({ ...prev, [subKey]: false }));
                }
              };

              return (
                <Box key={subKey}>
                  <Box
                    role="button"
                    onClick={onSubHeaderClick}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    p={2}
                    borderRadius="md"
                    bg={anyGrandChildActive ? 'blue.700' : 'transparent'}
                    color={anyGrandChildActive ? 'white' : 'inherit'}
                    _hover={{ bg: 'blue.700', color: 'white' }}
                    fontSize="sm"
                  >
                    <HStack spacing={3}>
                      <Box fontSize="lg" color={anyGrandChildActive ? 'white' : 'blue.500'}>
                        {iconNode}
                      </Box>
                      {!collapsed && <Text fontWeight="semibold">{ch.title}</Text>}
                    </HStack>
                    {!collapsed && (subIsOpen ? <FaChevronDown /> : <FaChevronRight />)}
                  </Box>
                  <Collapse in={subIsOpen}>
                    <VStack align="stretch" spacing={1} ml={collapsed ? 0 : 6} mt={1} mb={2}>
                      {ch.children.map((gc) => {
                        const gcActive = isNavItemActive({
                          item: gc,
                          pathname: currentPath,
                          isContabRoute,
                          contabActiveTab,
                        });
                        const gcTab = normalizeContabTab(extractTabFromLink(gc.link) || '');
                        const gcLockMeta = getAddonLockMeta
                          ? getAddonLockMeta(gc.addonCode || ch.addonCode || item.addonCode)
                          : null;
                        const gcLocked = Boolean(gcLockMeta?.locked || childLocked);
                        const lockMetaForGrandChild = gcLockMeta?.locked ? gcLockMeta : lockMetaForChild;
                        const isIvaChild = gcTab === 'iva' || gc.title?.toLowerCase().includes('iva');
                        const contabAddonMeta = addonStatusMap?.CONTAB_ADMIN;
                        const disabled =
                          isIvaChild &&
                          addonsLoading === false &&
                          contabAddonMeta &&
                          !contabAddonMeta.is_ri;
                        const tooltipLabel = disabled
                          ? 'Disponible solo para Responsable Inscripto'
                          : gcLocked
                          ? lockMetaForGrandChild?.message || 'Sin permisos para acceder.'
                          : '';
                        const isDisabledState = disabled || gcLocked;
                        const GcIconComp = gc.icon;
                        const gcIconNode = GcIconComp
                          ? React.isValidElement(GcIconComp)
                           ? GcIconComp
                            : <GcIconComp />
                          : <FaChevronRight />;
                        return (
                          <Tooltip
                            key={gc.title}
                            label={tooltipLabel}
                            isDisabled={!tooltipLabel}
                            placement="right"
                          >
                            <Box
                              as={Link}
                              to={gc.link}
                              onClick={(e) => {
                                e.preventDefault();
                                if (gcLocked) {
                                  onLockedAttempt?.(lockMetaForGrandChild);
                                  return;
                                }
                                if (disabled) return;
                                handleChildNavigate(gc.link, lockMetaForGrandChild);
                              }}
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              p={2}
                              borderRadius="md"
                              bg={gcActive ? 'blue.700' : 'transparent'}
                              color={gcActive ? 'white' : isDisabledState ? 'gray.500' : 'inherit'}
                              _hover={{
                                bg: isDisabledState ? 'transparent' : 'blue.700',
                                color: isDisabledState ? 'gray.500' : 'white',
                                cursor: isDisabledState ? 'not-allowed' : 'pointer',
                              }}
                              fontSize="sm"
                              opacity={isDisabledState ? 0.6 : 1}
                            >
                              <HStack spacing={3}>
                                <Box fontSize="lg" color={gcActive ? 'white' : isDisabledState ? 'gray.400' : 'blue.500'}>
                                  {gcIconNode}
                                </Box>
                                {!collapsed && <Text fontWeight="semibold">{gc.title}</Text>}
                              </HStack>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </VStack>
                  </Collapse>
                </Box>
              );
            }
            return (
              <Box
                key={ch.title}
                as={Link}
                to={ch.link}
                    onClick={(e) => {
                                e.preventDefault();
                                handleChildNavigate(ch.link, lockMetaForChild);
                              }}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p={2}
                  borderRadius="md"
                  bg={isActive ? 'blue.700' : 'transparent'}
                  color={isActive ? 'white' : 'inherit'}
                  _hover={{ bg: 'blue.700', color: 'white' }}
                  fontSize="sm"
                >
                  <HStack spacing={3} align={collapsed ? 'center' : 'flex-start'}>
                    <Box fontSize="lg" color={isActive ? 'white' : 'blue.500'}>
                      {iconNode}
                    </Box>
                    {!collapsed && (
                      <VStack spacing={0} align="flex-start">
                        <Text fontWeight="semibold">{ch.title}</Text>
                        {ch.description && (
                          <Text fontSize="xs" color={isActive ? 'whiteAlpha.800' : 'gray.500'}>
                            {ch.description}
                          </Text>
                        )}
                      </VStack>
                    )}
                  </HStack>
                  {childLocked && <FaLock color="#dd6b20" />}
                </Box>
              );
            })}
          </VStack>
        </Collapse>
      )}
    </Box>
  );
}

function ContextSwitcherControl({ uiContext, onChange, size = 'sm', fullWidth = false }) {
  const label =
    NAV_CONTEXT_META.find((contextItem) => contextItem.key === uiContext)?.label || 'Consorcios';
  const buttonBg = useColorModeValue('white', 'gray.900');
  const buttonColor = useColorModeValue('gray.800', 'white');
  const buttonBorder = useColorModeValue('blue.200', 'whiteAlpha.300');
  const buttonHoverBg = useColorModeValue('blue.50', 'whiteAlpha.200');
  const badgeBg = label === 'Mi Administración' ? 'purple.500' : 'blue.500';
  const textColor = useColorModeValue('gray.800', 'white');
  return (
    <Menu>
      <MenuButton
        as={Button}
        size={size}
        variant="outline"
        rightIcon={<FaChevronDown />}
        borderRadius="full"
        w={fullWidth ? '100%' : undefined}
        bg={buttonBg}
        color={buttonColor}
        borderColor={buttonBorder}
        _hover={{ bg: buttonHoverBg }}
      >
        <HStack spacing={2}>
          <Box
            w="24px"
            h="24px"
            borderRadius="full"
            bg={badgeBg}
            color="white"
            fontSize="sm"
            fontWeight="bold"
            alignItems="center"
            justifyContent="center"
            display="inline-flex"
          >
            {label.charAt(0)}
          </Box>
          <Text fontWeight="semibold" color={textColor}>
            {label}
          </Text>
        </HStack>
      </MenuButton>
      <MenuList>
        {NAV_CONTEXT_META.map((contextItem) => (
          <MenuItem
            key={contextItem.key}
            onClick={() => onChange?.(contextItem.key)}
            isDisabled={uiContext === contextItem.key}
          >
            <Text>{contextItem.label}</Text>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}
